/**
 * verse/transform.ts — Morphological Operators
 *
 * 12 operators. Each takes a kernel (a 2-4 word span extracted from
 * player text) and produces a single grammatical phrase. The planner
 * assembles these phrases into a complete response.
 *
 * This is NOT template substitution. The operators perform actual
 * linguistic transformations — nominalization, polarity inversion,
 * somatic grounding, embedding-based affect mapping — on the player's
 * own language. The response grows from the player's words the way a
 * reflection grows from a face: recognizable, but reorganized.
 *
 * Every output has been tested across a range of inputs. Broken grammar
 * is a hard failure here — "The lately of it" is a bug, not a feature.
 *
 * Operator taxonomy:
 *   Mirror     — ECHO, COMPRESS
 *   Linguistic — NOMINALIZE, WRAP, QUALIFY_SOFT
 *   Temporal   — TEMPORAL_HOLD, EVOLVE
 *   Reframe    — INVERT, INTERROGATE
 *   Somatic    — SITUATE_BODY, BODY_LOCATE
 *   Relational — ECHO_TOGETHER
 *   Connective — CHAIN, DEEPEN, SURPRISE
 *
 * CHAIN, DEEPEN, and SURPRISE are the compositional operators that break
 * the template ceiling. They don't pick from static pools — they compose
 * novel multi-clause thoughts by combining semantic primitives with the
 * previous segment's output. This is what makes the response space a
 * FIELD, not a LIBRARY.
 */

import { embedQuery } from "../neural/embeddings.js";
import { cosine } from "../neural/math.js";
import { scoreLexicon } from "../neural/affect-lexicon.js";
import type { Kernel } from "./kernel.js";
import type { ConversationArc } from "./discourse.js";
import * as W from "./whispers/index.js";

// ─── Context ─────────────────────────────────────────────────────────────────

export interface TransformContext {
  /** Full player text */
  playerText: string;
  /** Co-regulation strategy from cortex */
  strategy: "containment" | "activation" | "grounding" | "expansion";
  /** Vagal state */
  vagalState: "engaged" | "mobilized" | "shutdown";
  /** Affect dimensions 0-1 */
  tension: number;
  warmth: number;
  melancholy: number;
  curiosity: number;
  depth: number;
  playfulness: number;
  /** Personality 0-1 */
  patience: number;
  wit: number;
  /** Lowercase proper nouns from current text (to guard nominalize) */
  properNouns?: ReadonlySet<string>;
  /** Discourse echo — a kernel that has appeared before, if any */
  discourseEcho: { kernel: string; turnsAgo: number } | null;
  /** Relational subject detected in player text (e.g., "mom", "her") */
  relationalSubject?: string | null;
  /** Conversation arc — emotional territory of the conversation */
  arc?: ConversationArc | null;
  /** True if the player is ASKING, not asserting. Lets operators shape
   *  output differently: a statement "I feel lost" → ECHO returns "Lost."
   *  but a question "what is lost?" should never article-wrap the abstract. */
  inputIsQuestion?: boolean;
  /** True if the kernel's primary content word is a bare abstract concept
   *  being asked about ("what is love", "what is consciousness"). Operators
   *  that would otherwise article-wrap it should pivot to grappling shapes. */
  abstractInquiry?: boolean;
  /** Seeded PRNG for deterministic variation */
  rand: () => number;
  /** Texts of most-attended past messages — emotionally loaded priors */
  attentionHighlights?: string[];
  /** MeMo recalls surfaced this turn — memory insights to weave in */
  memoRecalls?: Array<{ summary: string; layer: string }>;
  /** Previous segment text — set during transformation for chaining operators */
  previousSegment?: string;
  /** Previous operator type — set during transformation for chaining operators */
  previousOperator?: OperatorType;
  /** All segments produced so far in the current response — enables recursive
   *  composition. CHAIN can reference any prior segment, DEEPEN can build on
   *  a CHAIN's output, SURPRISE can pivot off a DEEPEN. This is what makes
   *  the response a THOUGHT, not a LIST. */
  accumulatedSegments?: Array<{ text: string; operator: OperatorType }>;
  /** Predictive coding state — enables COUNTERFACTUAL operator to respond
   *  to prediction error gaps (what the player didn't say). */
  prediction?: { overallSurprise: number; isAnomaly: boolean; dominantLevel: string };
}

// ─── Operator Type ───────────────────────────────────────────────────────────

export type OperatorType =
  | "ECHO"
  | "NOMINALIZE"
  | "WRAP"
  | "TEMPORAL_HOLD"
  | "INVERT"
  | "SITUATE_BODY"
  | "COMPRESS"
  | "QUALIFY_SOFT"
  | "INTERROGATE"
  | "ECHO_TOGETHER"
  | "EVOLVE"
  | "BODY_LOCATE"
  | "CHAIN"
  | "DEEPEN"
  | "SURPRISE"
  | "COUNTERFACTUAL";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Pick one item from an array using ctx.rand() */
function pick<T>(arr: T[], rand: () => number): T {
  return arr[Math.floor(rand() * arr.length)];
}

/** Capitalize first letter of a string */
function cap(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

/** Re-capitalize a word if it's a known proper noun. Kernels are lowercased
 *  by tokenize(), so "john" needs to become "John" when placed in output. */
function recapProper(word: string): string {
  if (_activeProperNouns.has(word.toLowerCase())) return cap(word);
  return word;
}

const RELATIONAL_NOUNS = new Set([
  "mom", "mother", "dad", "father", "brother", "sister", "friend",
  "son", "daughter", "husband", "wife", "partner",
  "grandma", "grandmother", "grandpa", "grandfather", "baby", "child",
]);

/** Concrete nouns that should NEVER be nominalized or interrogated.
 *  These are PEOPLE, PLACES, THINGS — not emotional/abstract concepts.
 *  "The girlfriend of it" is nonsense. "The house of it" is nonsense.
 *  These words need engaged, contextual responses — not linguistic transformation. */
const CONCRETE_NOUNS = new Set([
  // People / relationships
  "girlfriend", "boyfriend", "wife", "husband", "partner", "friend",
  "mom", "mother", "dad", "father", "brother", "sister",
  "son", "daughter", "uncle", "aunt", "cousin", "grandma", "grandpa",
  "grandmother", "grandfather", "baby", "child", "kid", "children",
  "teacher", "boss", "coworker", "colleague", "doctor", "nurse",
  "therapist", "neighbor", "roommate", "ex", "fiance", "fiancee",
  // Animals
  "cat", "dog", "pet", "kitten", "puppy", "bird", "fish", "hamster",
  // Places
  "house", "home", "apartment", "room", "school", "work", "office",
  "hospital", "church", "store", "city", "town", "country", "park",
  "beach", "mountain", "garden", "street", "road", "building",
  // Things
  "car", "phone", "computer", "book", "door", "window", "bed",
  "table", "chair", "clothes", "money", "food", "water",
  // Events (concrete, not abstract)
  "visit", "trip", "vacation", "wedding", "party", "birthday",
  "funeral", "meeting", "date", "game", "concert", "dinner",
]);

/** "Your mom" not "The mom" */
function articleFor(word: string): string {
  return RELATIONAL_NOUNS.has(word) ? "Your" : "The";
}

/** Trim common punctuation from a word, preserving contractions */
function cleanWord(w: string): string {
  return w.toLowerCase().replace(/[^a-z']/g, "");
}

/**
 * Find the most content-heavy word in the kernel's words array.
 * Skips function words, prefers verbs/adjectives/nouns.
 */
const FUNCTION_WORDS = new Set([
  // Pronouns
  "i", "me", "my", "mine", "you", "your", "yours", "we", "our", "they", "their",
  "he", "she", "it", "its", "his", "her", "him", "them", "us",
  // Copulas / auxiliaries
  "the", "a", "an", "is", "am", "are", "was", "were", "be", "been", "being",
  "have", "has", "had", "do", "does", "did", "will", "would", "could", "should",
  "can", "may", "might", "shall", "must",
  // Prepositions
  "in", "on", "at", "to", "for", "with", "by", "from", "of", "about", "into",
  "after", "before", "during", "until", "since", "through", "between", "among",
  "away", "back", "down", "off", "over", "around", "along", "aside", "ahead", "apart",
  "together", "behind", "forward", "backward", "toward", "towards", "across", "upon",
  // Conjunctions / relative / interrogative
  "and", "or", "but", "if", "when", "where", "how", "what", "who", "which",
  "that", "this", "these", "those", "than", "whether", "why",
  // Adverbs / particles / fillers
  "not", "no", "nor", "so", "as", "up", "out", "just", "like", "even",
  "very", "really", "quite", "rather", "too", "also", "still", "yet",
  "actually", "anyway", "anyways", "basically", "definitely",
  "literally", "maybe", "perhaps", "probably", "seriously", "simply",
  "apparently", "obviously", "clearly", "honestly",
  "kind", "sort", "bit", "lot", "much", "many", "more", "most", "less", "least",
  "ever", "never", "always", "sometimes", "often", "already", "lately", "recently",
  "here", "there", "now", "then", "soon", "today", "yesterday", "tomorrow",
  // Contractions
  "don't", "won't", "can't", "didn't", "doesn't", "isn't", "wasn't", "weren't",
  "couldn't", "shouldn't", "wouldn't", "haven't", "hasn't", "hadn't", "aren't",
  "i'm", "i've", "i'd", "i'll", "you're", "you've", "you'd", "you'll",
  "we're", "they're", "it's", "that's", "there's", "here's", "what's",
  "who's", "how's", "he's", "she's",
  // Informal contractions
  "gonna", "gotta", "wanna", "kinda", "sorta",
  // Common verbs (too generic to be kernels)
  "get", "gets", "got", "getting", "going", "goes", "went", "go", "come", "came", "comes",
  "know", "think", "feel", "feels", "feeling", "felt",
  "tell", "telling", "told", "say", "saying", "said",
  "keep", "keeping", "kept", "make", "making", "made",
  "take", "taking", "took", "give", "giving", "gave",
  "see", "seeing", "saw", "mean", "meaning", "meant",
  "want", "wanting", "need", "needing", "try", "trying",
  "put", "set", "run", "bring", "brought", "sent", "left",
  "used", "using", "seems", "seemed", "looks", "looked",
  "started", "stopped", "ended", "began", "continued", "supposed",
  "woke", "woken", "slept", "ate", "drank",
  // Light verbs (action but no semantic weight)
  "let",
  // Indefinites / quantifiers
  "anything", "everything", "something", "nothing", "someone", "anyone",
  "everyone", "nobody", "somebody", "anybody", "everybody",
  "wherever", "whenever", "whatever", "however", "whichever",
  // Interjections (NOT content — too light for kernels)
  "hey", "hi", "hello", "yeah", "yep", "nope", "haha", "lol", "lmao",
  "hmm", "hm", "oh", "ah", "ugh", "wow", "okay", "ok", "sure", "right",
  "well", "fine", "alright",
  // Temporal/manner fillers
  "anymore", "ago", "years", "months", "weeks", "days", "hours", "minutes",
  "year", "month", "week", "day", "hour", "minute", "second", "seconds",
  "time", "times", "while", "moment", "period",
  "morning", "afternoon", "evening", "night", "tonight", "midnight", "noon",
  // Filler adjectives (do NOT include: alone, broken, lost, scared, real, etc.)
  "good", "bad", "great", "nice", "little", "big", "other", "another",
  "some", "any", "each", "every", "own", "same", "different",
  "first", "last", "next", "new", "old", "whole",
  // Vague nouns (no semantic anchor)
  "stuff", "things", "thing", "part", "place", "way", "type",
  // Misc
  "specific", "particular", "certain", "enough", "else", "only",
  "been", "being", "having", "doing",
  // ─── Conversational meta-words (talking about talking) ─────────────────
  "chat", "chatting", "talk", "talking", "conversation", "message",
  "response", "responses", "test", "testing", "tested", "model", "system",
  "version", "update", "question", "answer", "reply", "comment",
  "mention", "mentioned",
  // ─── Expletives as intensifiers (energy without semantic content) ──────
  "fuck", "fucken", "fuckin", "fucking", "shit", "shitty", "damn",
  "damned", "dammit", "hell", "crap", "crappy", "ass", "bullshit",
  "goddamn", "bloody", "freaking", "frickin",
  // ─── Technology / interface meta-discourse ─────────────────────────────
  "app", "program", "computer", "phone", "device", "screen", "click",
  "button", "feature", "bug", "error", "setting", "settings", "option",
  "options",
  // ─── Additional hedges / fillers ──────────────────────────────────────
  "mostly", "pretty", "super", "totally", "completely", "absolutely",
  "certainly", "exactly", "precisely", "guess", "suppose", "figured",
  "reckon", "dunno",
  // ─── Light verbs / adjectives (no emotional anchor) ───────────────────
  // NOTE: "real", "wrong", "funny" are in HIGH_VALUE_WORDS — do NOT add.
  "long", "short", "true", "false", "similar", "reasonable",
  "interesting", "cool", "awesome", "amazing", "wonderful", "fantastic",
  "neat", "weird", "crazy", "silly", "dumb", "stupid",
  "better",
]);

function contentWords(words: string[]): string[] {
  return words.map(cleanWord).filter(w => w.length > 0 && !FUNCTION_WORDS.has(w)).map(recapProper);
}

function primaryContent(kernel: Kernel): string {
  const content = contentWords(kernel.words);
  return content.length > 0 ? content[0] : recapProper(cleanWord(kernel.words[0]));
}

// ─── Affect-Noun Vocabulary (WRAP) ───────────────────────────────────────────
// ~50 phenomenological nouns. Embedded lazily, cached.

const AFFECT_NOUNS = [
  // Original core
  "groundlessness", "heaviness", "lightness", "emptiness", "fullness",
  "closeness", "distance", "absence", "presence", "stillness",
  "restlessness", "uncertainty", "clarity", "warmth", "coldness",
  "tenderness", "vulnerability", "safety", "fragility", "longing",
  "contentment", "turbulence", "peace", "chaos", "silence",
  "depth", "weight", "weightlessness", "spaciousness", "tightness",
  "openness", "darkness", "brightness", "loneliness", "togetherness",
  "softness", "roughness", "familiarity", "strangeness", "recognition",
  "ache", "gentleness", "fierceness", "weariness", "aliveness",
  "numbness", "hollowness", "steadiness", "unraveling", "disconnection",
  // Relational
  "intimacy", "attachment", "abandonment", "betrayal", "devotion",
  "loyalty", "kinship", "belonging", "exile", "rejection",
  "acceptance", "trust", "distrust", "connection", "reunion",
  // Temporal
  "permanence", "impermanence", "urgency", "patience", "anticipation",
  "nostalgia", "regret", "momentum", "stagnation", "suspension",
  "finality", "eternity", "ephemerality", "transience",
  // Cognitive
  "confusion", "understanding", "bewilderment", "realization", "denial",
  "obsession", "fixation", "rumination", "insight", "blindness",
  // Physical / somatic
  "exhaustion", "vitality", "tension", "relaxation", "constriction",
  "expansion", "breathlessness", "drowning", "floating", "grounding",
  "vertigo", "paralysis", "trembling",
  // Existential
  "meaninglessness", "purpose", "absurdity", "wonder", "awe",
  "dread", "insignificance", "grandeur", "mortality", "infinity",
  "void", "plenitude",
  // ─── Expanded: grief / loss ──────────────────────────────────────────────
  "grief", "mourning", "bereavement", "heartbreak", "yearning", "anguish",
  "sorrow", "desolation", "devastation",
  // ─── Expanded: hope / recovery ───────────────────────────────────────────
  "resilience", "emergence", "renewal", "restoration", "relief", "recovery",
  "mending", "rebuilding", "redemption",
  // ─── Expanded: confusion / disorientation ────────────────────────────────
  "disorientation", "fog", "haze", "opacity", "murk",
  // ─── Expanded: connection / communion ────────────────────────────────────
  "communion", "resonance", "solidarity", "rapport", "entanglement",
  "companionship", "affinity", "mutuality",
  // ─── Expanded: power / agency ────────────────────────────────────────────
  "sovereignty", "autonomy", "defiance", "resistance", "resolve",
  "determination", "agency", "empowerment",
  // ─── Expanded: shame / exposure ──────────────────────────────────────────
  "exposure", "nakedness", "humiliation", "inadequacy",
  "self-contempt", "unworthiness", "mortification",
  // ─── Expanded: sacred / awe ──────────────────────────────────────────────
  "reverence", "transcendence", "grace", "sanctity",
  "holiness", "sublimity", "numinousness",
  // ─── Expanded: ambivalence / conflict ────────────────────────────────────
  "ambivalence", "conflict", "contradiction",
  "torn", "dissonance", "paradox",
];

const affectNounCache = new Map<string, Float32Array>();

function getNounEmbedding(noun: string): Float32Array {
  if (!affectNounCache.has(noun)) {
    affectNounCache.set(noun, embedQuery(noun));
  }
  return affectNounCache.get(noun)!;
}

// Nouns that imply improvement/resolution — wrong when someone is IN the feeling.
// "I feel alone" → "that recovery" is invalidating.
// Nouns that contradict distress states — don't tell someone who feels alone
// that what they're feeling is "companionship" or "togetherness."
const RECOVERY_NOUNS = new Set([
  "recovery", "resilience", "emergence", "renewal", "restoration", "relief",
  "mending", "rebuilding", "redemption", "empowerment", "resolution",
  "contentment", "peace", "togetherness", "safety", "plenitude",
  "companionship", "communion", "rapport", "solidarity", "mutuality",
  "affinity", "joy", "contentment", "serenity", "gratitude",
  "lightness", "brightness", "fullness", "spaciousness", "openness",
]);

function closestAffectNoun(kernel: Kernel, ctx?: TransformContext): { noun: string; similarity: number } {
  let best = { noun: AFFECT_NOUNS[0], similarity: -1 };
  // Filter: when melancholy/tension is high or strategy is containment/grounding,
  // suppress recovery-flavored nouns. Don't tell someone who's hurting that
  // the feeling they're having is "relief."
  const suppressRecovery = ctx && (
    ctx.melancholy > 0.35 ||
    ctx.strategy === "containment" ||
    (ctx.strategy === "grounding" && ctx.tension > 0.4)
  );
  for (const noun of AFFECT_NOUNS) {
    if (suppressRecovery && RECOVERY_NOUNS.has(noun)) continue;
    const emb = getNounEmbedding(noun);
    const sim = cosine(kernel.embedding, emb);
    if (sim > best.similarity) best = { noun, similarity: sim };
  }
  return best;
}

// ─── Nominalize Helpers ───────────────────────────────────────────────────────

const IRREGULAR_NOMINALS: Record<string, string> = {
  lost: "loss", lose: "loss", losing: "loss",
  afraid: "fear", scared: "fear", fear: "fear",
  angry: "anger", rage: "rage", furious: "fury",
  dead: "death", passed: "passing", pass: "passing",
  happy: "joy", glad: "joy",
  sad: "sadness", grief: "grief",
  broken: "breaking", breaking: "breaking",
  gone: "absence", missing: "absence",
  empty: "emptiness",
  alone: "aloneness", lonely: "loneliness",
  tired: "exhaustion", exhausted: "exhaustion",
  stuck: "stuckness",
  fall: "falling",
  disappear: "disappearing", disappeared: "disappearing",
  collapse: "collapsing", collapsed: "collapsing",
  sorry: "sorrow", real: "realness", funny: "humor",
  strange: "strangeness", weird: "strangeness", wrong: "wrongness",
  free: "freedom", trapped: "entrapment", safe: "safety",
  brave: "courage", strong: "strength", weak: "weakness",
  better: "healing", worse: "worsening",
  whole: "wholeness", awake: "waking", alive: "aliveness",
  quiet: "quietness", warm: "warmth", cold: "coldness",
  dark: "darkness", deep: "depth", vast: "vastness",
  dream: "dreaming", wonder: "wondering", consciousness: "awareness",
  love: "loving", hate: "hatred", hope: "hoping", trust: "trusting",
  beautiful: "beauty", terrible: "terror", horrible: "horror",
  off: "unease", grey: "greyness", gray: "greyness",
  miss: "missing", listen: "listening", listens: "listening",
  care: "caring", matter: "mattering", matters: "mattering",
  terrified: "terror", peaceful: "peace",
  forget: "forgetting", forgotten: "forgetting", forgive: "forgiveness",
  understand: "understanding", belong: "belonging",
  // Consonant-doubling verbs (CVC pattern: sit→sitting, get→getting, etc.)
  // NOTE: run/running, begin/beginning, win/winning are defined in later
  // semantic sections (voice, beginning/ending, winning/losing) and must NOT
  // be duplicated here or the later override is silently lost.
  sit: "sitting", sat: "sitting", sitting: "sitting",
  get: "getting", got: "getting", getting: "getting",
  cut: "cutting", cutting: "cutting",
  hit: "hitting", hitting: "hitting",
  put: "putting", putting: "putting",
  stop: "stopping", stopped: "stopping", stopping: "stopping",
  drop: "dropping", dropped: "dropping", dropping: "dropping",
  swim: "swimming", swam: "swimming", swimming: "swimming",
  dig: "digging", dug: "digging", digging: "digging",
  grab: "grabbing", grabbed: "grabbing", grabbing: "grabbing",
  hug: "hugging", hugged: "hugging", hugging: "hugging",
  bet: "betting", betting: "betting",
  let: "letting", letting: "letting",
  deserve: "deserving", believe: "believing", regret: "regretting",
  mourn: "mourning", grieve: "grieving", worry: "worrying",
  doubt: "doubting", bright: "brightness", clear: "clarity",
  close: "closeness", distant: "distance", present: "presence",
  absent: "absence", visible: "visibility", invisible: "invisibility",
  // miss / sense
  longing: "longing", heard: "hearing", seen: "seeing",
  // memory
  remember: "remembering", forgot: "forgetting",
  remembered: "remembering", remembering: "remembering",
  // change / movement
  changed: "change", change: "change",
  left: "leaving", leave: "leaving", leaving: "leaving",
  stayed: "staying", stay: "staying", staying: "staying",
  // emotion in motion
  hurting: "hurt", hurt: "hurt",
  crying: "tears", cry: "tears",
  laughing: "laughter", laugh: "laughter",
  // sleep / waking
  sleeping: "sleep", sleep: "sleep",
  waking: "awakening", woke: "awakening", awakening: "awakening",
  running: "flight", run: "flight",
  // concealment / seeking
  hiding: "concealment", hide: "concealment",
  seeking: "search", seek: "search",
  finding: "discovery", find: "discovery", found: "discovery",
  // vertical movement
  falling: "descent", descent: "descent",
  rising: "ascent", rise: "ascent", ascent: "ascent",
  sinking: "sinking", sink: "sinking",
  // voice
  screaming: "scream", scream: "scream",
  whispering: "whisper", whisper: "whisper",
  // body
  bleeding: "blood", bleed: "blood",
  breathing: "breath", breath: "breath", breathe: "breath",
  // life cycle
  growing: "growth", grow: "growth", growth: "growth",
  die: "death", died: "death", dying: "death",
  living: "life", life: "life",
  // knowing / mystery
  known: "knowing", knowing: "knowing",
  unknown: "mystery",
  // touch / holding
  touching: "touch", touch: "touch",
  holding: "embrace", hold: "embrace", embrace: "embrace",
  // winning / losing
  winning: "victory", win: "victory", victory: "victory",
  // beginning / ending
  beginning: "start", begin: "start", start: "start",
  ending: "conclusion", end: "conclusion", conclusion: "conclusion",
  // promise / fracture
  promising: "promise", promise: "promise",
  fracture: "fracture",
  // desire / need / hunger
  wanting: "desire", desire: "desire",
  needing: "need", need: "need",
  craving: "hunger", crave: "hunger", hunger: "hunger",
  // acceptance / refusal
  accepting: "acceptance", acceptance: "acceptance",
  refusing: "refusal", refuse: "refusal", refusal: "refusal",
  // pretense / revelation
  pretending: "pretense", pretend: "pretense", pretense: "pretense",
  revealing: "revelation", reveal: "revelation", revelation: "revelation",
  // water imagery
  drowning: "submersion", drown: "submersion",
  surfacing: "emergence", surface: "emergence", emerging: "emergence",
  // scatter / gather
  scattered: "scattering", scatter: "scattering",
  gathering: "gathering", gather: "gathering",
  // texture states → nouns
  tight: "tightness", loose: "looseness",
  rigid: "rigidity", fluid: "fluidity",
  // nervous states
  restless: "restlessness",
  anxious: "anxiety", anxiety: "anxiety",
  nervous: "nervousness",
  overwhelmed: "overwhelm", overwhelm: "overwhelm",
  underwhelmed: "flatness",
  // shattering / crumbling
  shattered: "shattering", shattering: "shattering",
  crumbling: "crumbling", crumble: "crumbling",
  // fading / blooming / withering
  fading: "fading", fade: "fading",
  blooming: "bloom", bloom: "bloom",
  withering: "withering", wither: "withering",
  // fire / ice
  burning: "burning", burn: "burning",
  freezing: "frost", freeze: "frost", frost: "frost",
  // pain signals
  aching: "ache",
  throbbing: "pulse", throb: "pulse",
  stinging: "sting", sting: "sting",
  // certainty / confusion
  confused: "confusion", confusion: "confusion",
  certain: "certainty", certainty: "certainty",
  unsure: "uncertainty", uncertainty: "uncertainty",
  // moral / social emotion
  proud: "pride", pride: "pride",
  ashamed: "shame", shame: "shame",
  guilty: "guilt", guilt: "guilt",
  innocent: "innocence", innocence: "innocence",
  // ─── Irregular past-tense → gerund forms ─────────────────────────────────
  knew: "knowing", thought: "thinking", fought: "fighting",
  taught: "teaching", caught: "catching",
  held: "holding", felt: "feeling", bled: "bleeding",
  stood: "standing",
  sang: "singing",
  grew: "growing", flew: "flying", drew: "drawing",
  spoke: "speaking", chose: "choosing",
  wore: "wearing", tore: "tearing", bore: "bearing",
  swore: "swearing", froze: "freezing", rose: "rising",
  fell: "falling", built: "building", meant: "meaning",
  kept: "keeping", wept: "weeping", crept: "creeping",
  swept: "sweeping", dealt: "dealing", knelt: "kneeling",
  leapt: "leaping", broke: "breaking",
  // ─── Common verbs (base + past) not in FUNCTION_WORDS ───────────────────
  ate: "eating", eat: "eating", eaten: "eating",
  cook: "cooking", cooked: "cooking",
  taste: "tasting", tasted: "tasting",
  watch: "watching", watched: "watching",
  smell: "smelling", smelled: "smelling",
  walk: "walking", walked: "walking",
  dance: "dancing", danced: "dancing",
  play: "playing", played: "playing",
  smile: "smiling", smiled: "smiling",
  kiss: "kissing", kissed: "kissing",
  write: "writing", wrote: "writing",
  read: "reading", draw: "drawing",
  sing: "singing", work: "working", worked: "working",
  // ─── Verbs with natural -tion/-ation/-ance noun forms ──────────────────
  admire: "admiration", admired: "admiration", admiring: "admiration",
  thank: "gratitude", thanked: "gratitude", thanking: "gratitude",
  communicate: "communication", communicated: "communication",
  celebrate: "celebration", celebrated: "celebration",
  prepare: "preparation", prepared: "preparation",
  explore: "exploration", explored: "exploration",
  capture: "capture", captured: "capture",
  inspire: "inspiration", inspired: "inspiration",
  imagine: "imagination", imagined: "imagination",
  create: "creation", created: "creation",
  appreciate: "appreciation", appreciated: "appreciation", appreciating: "appreciation",
  participate: "participation", participated: "participation",
  educate: "education", educated: "education",
  meditate: "meditation", meditated: "meditation",
  contemplate: "contemplation", contemplated: "contemplation",
  hesitate: "hesitation", hesitated: "hesitation",
  navigate: "navigation", navigated: "navigation",
  cultivate: "cultivation", cultivated: "cultivation",
  fascinate: "fascination", fascinated: "fascination",
  demonstrate: "demonstration", demonstrated: "demonstration",
  generate: "generation", generated: "generation",
  separate: "separation", separated: "separation",
  tolerate: "tolerance", tolerated: "tolerance",
  dominate: "dominance", dominated: "dominance",
  liberate: "liberation", liberated: "liberation",
  illuminate: "illumination", illuminated: "illumination",
  accumulate: "accumulation", accumulated: "accumulation",
  devote: "devotion", devoted: "devotion",
  invite: "invitation", invited: "invitation",
  decide: "decision", decided: "decision",
  collide: "collision", collided: "collision",
  erode: "erosion", eroded: "erosion",
  explode: "explosion", exploded: "explosion",
  intrude: "intrusion", intruded: "intrusion",
  exclude: "exclusion", excluded: "exclusion",
  include: "inclusion", included: "inclusion",
  conclude: "conclusion", concluded: "conclusion",
  confuse: "confusion",
  compose: "composition", composed: "composition",
  expose: "exposure", exposed: "exposure",
  oppose: "opposition", opposed: "opposition",
  propose: "proposal", proposed: "proposal",
  suppose: "supposition", supposed: "supposition",
  dispose: "disposition", disposed: "disposition",
  observe: "observation", observed: "observation",
  preserve: "preservation", preserved: "preservation",
  reserve: "reservation", reserved: "reservation",
  resolve: "resolution", resolved: "resolution",
  revolve: "revolution", revolved: "revolution",
  involve: "involvement", involved: "involvement",
  evolve: "evolution", evolved: "evolution",
  dissolve: "dissolution", dissolved: "dissolution",
  absorb: "absorption", absorbed: "absorption",
  describe: "description", described: "description",
  prescribe: "prescription", prescribed: "prescription",
  transcribe: "transcription", transcribed: "transcription",
  assume: "assumption", assumed: "assumption",
  consume: "consumption", consumed: "consumption",
  resume: "resumption", resumed: "resumption",
  receive: "reception", received: "reception",
  perceive: "perception", perceived: "perception",
  conceive: "conception", conceived: "conception",
  deceive: "deception", deceived: "deception",
  achieve: "achievement", achieved: "achievement",
  recognize: "recognition", recognized: "recognition",
  organize: "organization", organized: "organization",
  realize: "realization", realized: "realization",
  apologize: "apology", apologized: "apology",
  sympathize: "sympathy", sympathized: "sympathy",
  empathize: "empathy", empathized: "empathy",
  survive: "survival", survived: "survival",
  arrive: "arrival", arrived: "arrival",
  deprive: "deprivation", deprived: "deprivation",
  derive: "derivation", derived: "derivation",
  thrive: "thriving", thrived: "thriving",
  strive: "striving", strived: "striving", strove: "striving",
  connect: "connection", connected: "connection",
  protect: "protection", protected: "protection",
  reflect: "reflection", reflected: "reflection",
  respect: "respect", respected: "respect",
  expect: "expectation", expected: "expectation",
  reject: "rejection", rejected: "rejection",
  neglect: "neglect", neglected: "neglect",
  collect: "collection", collected: "collection",
  direct: "direction", directed: "direction",
  select: "selection", selected: "selection",
  detect: "detection", detected: "detection",
  suspect: "suspicion", suspected: "suspicion",
  affect: "effect", affected: "effect",
  infect: "infection", infected: "infection",
  attract: "attraction", attracted: "attraction",
  distract: "distraction", distracted: "distraction",
  extract: "extraction", extracted: "extraction",
  subtract: "subtraction", subtracted: "subtraction",
  construct: "construction", constructed: "construction",
  destruct: "destruction",
  obstruct: "obstruction", obstructed: "obstruction",
  instruct: "instruction", instructed: "instruction",
  possess: "possession", possessed: "possession",
  express: "expression", expressed: "expression",
  impress: "impression", impressed: "impression",
  suppress: "suppression", suppressed: "suppression",
  depress: "depression", depressed: "depression",
  confess: "confession", confessed: "confession",
  obsess: "obsession", obsessed: "obsession",
  permit: "permission", permitted: "permission",
  submit: "submission", submitted: "submission",
  admit: "admission", admitted: "admission",
  commit: "commitment", committed: "commitment",
  transmit: "transmission", transmitted: "transmission",
  emit: "emission", emitted: "emission",
  compete: "competition", competed: "competition",
  complete: "completion", completed: "completion",
  repeat: "repetition", repeated: "repetition",
  delete: "deletion", deleted: "deletion",
  deplete: "depletion", depleted: "depletion",
  define: "definition", defined: "definition",
  confine: "confinement", confined: "confinement",
  combine: "combination", combined: "combination",
  determine: "determination", determined: "determination",
  examine: "examination", examined: "examination",
  intervene: "intervention", intervened: "intervention",
  convene: "convention", convened: "convention",
  redeem: "redemption", redeemed: "redemption",
  condemn: "condemnation", condemned: "condemnation",
  transform: "transformation", transformed: "transformation",
  perform: "performance", performed: "performance",
  inform: "information", informed: "information",
  conform: "conformity", conformed: "conformity",
  reform: "reformation", reformed: "reformation",
  deform: "deformation", deformed: "deformation",
  produce: "production", produced: "production",
  reduce: "reduction", reduced: "reduction",
  introduce: "introduction", introduced: "introduction",
  reproduce: "reproduction", reproduced: "reproduction",
  seduce: "seduction", seduced: "seduction",
  translate: "translation", translated: "translation",
  migrate: "migration", migrated: "migration",
  relate: "relation", related: "relation",
  isolate: "isolation", isolated: "isolation",
  violate: "violation", violated: "violation",
  manipulate: "manipulation", manipulated: "manipulation",
  stimulate: "stimulation", stimulated: "stimulation",
  speculate: "speculation", speculated: "speculation",
  circulate: "circulation", circulated: "circulation",
  regulate: "regulation", regulated: "regulation",
  simulate: "simulation", simulated: "simulation",
  assimilate: "assimilation", assimilated: "assimilation",
  annihilate: "annihilation", annihilated: "annihilation",
  deteriorate: "deterioration", deteriorated: "deterioration",
  cooperate: "cooperation", cooperated: "cooperation",
  elaborate: "elaboration", elaborated: "elaboration",
  evaporate: "evaporation", evaporated: "evaporation",
  anticipate: "anticipation", anticipated: "anticipation",
  accelerate: "acceleration", accelerated: "acceleration",
  penetrate: "penetration", penetrated: "penetration",
  concentrate: "concentration", concentrated: "concentration",
  integrate: "integration", integrated: "integration",
  disintegrate: "disintegration", disintegrated: "disintegration",
  negotiate: "negotiation", negotiated: "negotiation",
  associate: "association", associated: "association",
  exaggerate: "exaggeration", exaggerated: "exaggeration",
  frustrate: "frustration", frustrated: "frustration",
  compensate: "compensation", compensated: "compensation",
  devastate: "devastation", devastated: "devastation",
  accommodate: "accommodation", accommodated: "accommodation",
  suffocate: "suffocation", suffocated: "suffocation",
  intoxicate: "intoxication", intoxicated: "intoxication",
  complicate: "complication", complicated: "complication",
  authenticate: "authentication", authenticated: "authentication",
  investigate: "investigation", investigated: "investigation",
  eliminate: "elimination", eliminated: "elimination",
  originate: "origin", originated: "origin",
  terminate: "termination", terminated: "termination",
  coordinate: "coordination", coordinated: "coordination",
  subordinate: "subordination", subordinated: "subordination",
  // ─── Verbs ending in consonant clusters: just +ing ─────────────────────
  point: "pointing", pointed: "pointing",
  demand: "demand", demanded: "demand",
  command: "command", commanded: "command",
  expand: "expansion", expanded: "expansion",
  defend: "defense", defended: "defense",
  offend: "offense", offended: "offense",
  attend: "attention", attended: "attention",
  intend: "intention", intended: "intention",
  extend: "extension", extended: "extension",
  suspend: "suspension", suspended: "suspension",
  respond: "response", responded: "response",
  correspond: "correspondence", corresponded: "correspondence",
  recommend: "recommendation", recommended: "recommendation",
  comprehend: "comprehension", comprehended: "comprehension",
  apprehend: "apprehension", apprehended: "apprehension",
  transcend: "transcendence", transcended: "transcendence",
  descend: "descent", descended: "descent",
  ascend: "ascent", ascended: "ascent",
  depend: "dependence", depended: "dependence",
  blend: "blend", blended: "blend",
  mend: "mending", mended: "mending",
  rend: "rending",
  tend: "tendency", tended: "tendency",
  lend: "lending", lent: "lending",
  send: "sending", sent: "sending",
  spend: "spending", spent: "spending",
  bend: "bending", bent: "bending",
  wend: "wending",
  fend: "defense",
  vend: "vending",
};

/**
 * Produce a noun form for a word.
 * - Irregular map first
 * - Verbs: -ing form ("the Xing of it")
 * - Adjectives / nouns: -ness fallback
 * - Already ends in -ing or -ness: use as-is
 */
// Module-level proper noun set — populated by each operator before calling nominalize.
// This avoids threading the set through 15+ call sites.
let _activeProperNouns: ReadonlySet<string> = new Set();

// Verbs that SHOULD be gerundified when nominalize encounters them.
// This is the authoritative list. If a word isn't here, in IRREGULAR_NOMINALS,
// or recognizable as past tense (-ed/-ied), nominalize returns it unchanged.
// This prevents "superhumaning", "purposing", "ninjaing", "topicing", etc.
const GERUNDIFIABLE = new Set([
  // Emotional / relational verbs
  "miss", "hold", "burn", "drown", "float", "fly", "run", "hide",
  "sink", "fall", "break", "cry", "scream", "whisper", "dream",
  "trust", "hate", "love", "leave", "stay", "forget", "remember",
  "forgive", "abandon", "betray", "hope", "wish", "want", "need",
  "hurt", "ache", "bleed", "suffer", "endure", "survive", "kill",
  "die", "live", "exist", "matter", "shatter", "crumble", "collapse",
  "vanish", "disappear", "wonder", "believe", "pray", "mourn",
  "grieve", "rage", "tremble", "shake", "wait", "search", "reach",
  "climb", "crawl", "struggle", "fight", "resist", "surrender",
  "breathe", "heal", "mend", "build", "grow", "change", "become",
  // Sensory / domestic verbs
  "cook", "taste", "smell", "laugh", "smile", "dance", "sing",
  "hug", "kiss", "touch", "hear", "listen", "watch", "stare", "gaze",
  // Action verbs with emotional weight
  "carry", "drag", "pull", "push", "pour", "spill", "scatter",
  "gather", "weave", "stitch", "carve", "paint", "write", "draw",
  "erase", "destroy", "create", "lose", "find", "choose", "refuse",
]);

function nominalize(word: string): string {
  // Never nominalize proper nouns — "John" must never become "johning"
  if (/^[A-Z][a-z]/.test(word)) return word;
  // Also guard lowercase proper nouns (kernel words are already lowercased by tokenize)
  if (_activeProperNouns.has(word.toLowerCase())) return word;
  const w = cleanWord(word.toLowerCase());

  // 1. Irregular map — authoritative override (covers past tense, adjective→noun, etc.)
  if (IRREGULAR_NOMINALS[w]) return IRREGULAR_NOMINALS[w];

  // 2. Already nominal forms — return as-is
  if (w.endsWith("ing")) return w;
  if (w.endsWith("ness")) return w;
  if (w.endsWith("tion") || w.endsWith("sion") || w.endsWith("ment")) return w;
  if (NOUN_LIKE.has(w)) return w;

  // 3. Past tense -ied: definitely a verb → gerundify (cried → crying)
  if (w.endsWith("ied") && w.length > 4) return w.slice(0, -3) + "ying";

  // 4. Past tense -ed: definitely a verb → gerundify (tasted → tasting)
  if (w.endsWith("ed") && w.length > 3) {
    const stem = w.slice(0, -2);
    if (stem.length >= 2) return stem + "ing";
  }

  // 5. Known gerundifiable verbs → apply morphological rules
  if (GERUNDIFIABLE.has(w)) {
    return gerundify(w);
  }

  // 6. DEFAULT: return unchanged.
  // This is the critical safety rule. Unknown words are overwhelmingly nouns,
  // adjectives, or already-conjugated forms. Gerundifying them produces garbage:
  // "toileting", "brainsing", "sandwichesing", "neighborhooding".
  // Only GERUNDIFIABLE (step 5) and IRREGULAR_NOMINALS (step 1) should transform.
  // If a word needs gerund form, add it to GERUNDIFIABLE explicitly.
  return w;
}

/** Check if a word has a suffix that marks it as a noun or adjective (not a verb).
 *  Conservative: only suffixes that are unambiguously nominal. Short suffixes like
 *  -er/-or/-ar are excluded because they're common on verbs too (deliver, consider). */
function _isNominalSuffix(w: string): boolean {
  // Three-char suffixes (unambiguously nominal/adjectival)
  if (w.endsWith("ity") || w.endsWith("ous") || w.endsWith("ful") || w.endsWith("ism")
    || w.endsWith("ist") || w.endsWith("dom") || w.endsWith("acy") || w.endsWith("ogy")
    || w.endsWith("phy") || w.endsWith("thy") || w.endsWith("sis") || w.endsWith("ary")
    || w.endsWith("ery") || w.endsWith("ory")) return true;
  // Four+ char suffixes
  if (w.endsWith("ance") || w.endsWith("ence") || w.endsWith("ship") || w.endsWith("ical")
    || w.endsWith("ally") || w.endsWith("less") || w.endsWith("like") || w.endsWith("ward")
    || w.endsWith("wise") || w.endsWith("able") || w.endsWith("ible") || w.endsWith("ular")
    || w.endsWith("ious") || w.endsWith("eous") || w.endsWith("ness") || w.endsWith("ment")
    || w.endsWith("ual") || w.endsWith("ial")) return true;
  // Two-char: only very safe ones (adjective-only endings)
  if (w.endsWith("ly") || w.endsWith("ic")) return true;
  // Protect words ending in -man/-men (superhuman, fireman) — clearly not verbs
  if (w.endsWith("man") || w.endsWith("men")) return true;
  return false;
}

/** Apply English morphological rules to turn a verb stem into its -ing form. */
function gerundify(w: string): string {
  // E-dropping: admire→admiring, celebrate→celebrating, capture→capturing
  // Exception: ee (see→seeing, flee→fleeing), ye (dye→dyeing)
  if (w.endsWith("e") && !w.endsWith("ee") && !w.endsWith("ye") && w.length > 3) {
    return w.slice(0, -1) + "ing";
  }
  // CVC consonant-doubling: sit→sitting, run→running, swim→swimming
  // Only for single-syllable or stress-final words ending in single consonant after single vowel
  const VOWELS = "aeiou";
  if (w.length >= 3) {
    const last = w[w.length - 1];
    const penult = w[w.length - 2];
    const antepenult = w[w.length - 3];
    if (last && penult && antepenult
      && !VOWELS.includes(last) && last !== "w" && last !== "x" && last !== "y"
      && VOWELS.includes(penult) && !VOWELS.includes(antepenult)) {
      // Only double for short (single-syllable) words to avoid "visiting" → "visitting"
      // Heuristic: double only if the word is ≤5 chars (mostly monosyllabic)
      if (w.length <= 5) {
        return w + last + "ing";
      }
    }
  }
  // Default: just add -ing (thank→thanking, understand→understanding)
  return w + "ing";
}

// ─── 1. ECHO ──────────────────────────────────────────────────────────────────

/** Words that work as standalone echoes ("Alone." "Empty." "Broken.") */
const ECHOABLE_ADJECTIVES = new Set([
  // Original core
  "alone", "empty", "broken", "lost", "tired", "stuck", "numb", "hollow",
  "heavy", "dark", "cold", "quiet", "still", "gone", "dead", "real",
  "scared", "afraid", "angry", "hurt", "sad", "lonely", "helpless",
  "safe", "warm", "free", "alive", "open", "light", "grey", "gray",
  // Distress / vulnerability
  "desperate", "exhausted", "shattered", "crushed", "overwhelmed",
  "devastated", "fragile", "vulnerable", "raw", "exposed",
  "invisible", "forgotten", "worthless", "hopeless",
  "furious", "bitter", "resentful", "jealous", "envious",
  "ashamed", "guilty", "proud", "grateful", "blessed",
  // Calm / unsettled
  "peaceful", "serene", "restless", "anxious", "nervous",
  "paranoid", "confused", "certain", "unsure",
  // State / condition
  "trapped", "caged", "wild", "tame", "fierce", "gentle",
  "tender", "rough", "sharp", "dull", "flat",
  "bright", "dim", "faint", "loud", "silent",
  "transparent", "opaque", "solid", "hollow", "full", "blank",
  // Movement / embodied state
  "rigid", "fluid", "floating", "sinking", "rising", "falling",
  "crumbling", "unraveling", "burning", "frozen",
  "dizzy", "grounded", "untethered", "anchored", "drifting",
  // Electric / sensory
  "electrified",
  // Gerund / participial states (emotional shorthand: "Laughing." "Crying." "Waiting.")
  "missing", "laughing", "crying", "dreaming", "waking", "leaving", "staying",
  "waiting", "hoping", "healing", "hurting", "starting", "wondering",
]);

/** Words that work with "The X." article pattern */
const NOUN_LIKE = new Set([
  // Original core
  "floor", "room", "wall", "door", "sky", "night", "morning", "silence",
  "darkness", "light", "weight", "emptiness", "loneliness", "fear",
  "anger", "sadness", "joy", "pain", "love", "hope", "dream", "truth",
  "consciousness", "absence", "distance", "quiet", "cold", "warmth",
  // Emotional / relational
  "grief", "loss", "longing", "desire", "hunger", "thirst",
  "breath", "blood", "bone", "skin", "wound", "scar", "face", "voice", "name",
  "mirror", "shadow", "echo", "ghost", "memory",
  "home", "exile", "refuge", "shelter",
  // Weather / nature
  "storm", "rain", "thunder", "ocean", "river", "mountain",
  "earth", "fire", "water", "wind", "stone",
  "glass", "smoke", "dust", "ash", "flame",
  // Sky / cosmos
  "star", "moon", "sun", "void", "abyss",
  // Spatial / threshold
  "edge", "boundary", "threshold", "bridge", "window", "horizon",
  "path", "wall",
  // Landscape
  "forest", "garden", "wilderness", "desert", "island",
  // Confinement / release
  "prison", "freedom", "cage", "key", "lock",
  // Growth / organic
  "root", "seed", "branch", "leaf", "flower", "thorn",
  // People / relational (so operators say "Your mom." not "Moming.")
  "mom", "mother", "dad", "father", "brother", "sister",
  "friend", "son", "daughter", "husband", "wife", "partner",
  "grandmother", "grandfather", "grandma", "grandpa", "baby", "child",
  // Kitchen / domestic
  "recipe", "kitchen", "table", "chair", "bed", "pillow", "blanket",
  "cup", "plate", "food", "meal", "dinner", "breakfast", "lunch",
  "coffee", "tea", "oven", "stove",
  // House / place
  "house", "apartment", "porch", "hallway", "attic", "basement", "closet",
  "roof", "stairs", "fence", "yard",
  // Time / season
  "evening", "afternoon", "birthday", "holiday",
  "summer", "winter", "spring", "autumn",
  // Body (beyond wound/scar/face/voice/skin/blood/bone already present)
  "hair", "eyes", "hands", "arms", "legs", "heart", "head",
  // Emotional objects / keepsakes
  "letter", "photo", "photograph", "ring", "necklace", "gift",
  "song", "story", "poem", "prayer", "promise", "secret", "wish",
  "diary", "journal", "ribbon", "candle",
  // Common nouns without recognizable suffixes (protect from gerundification)
  "purpose", "world", "thing", "stuff", "ninja", "topic", "spirit",
  "power", "color", "weather", "music", "noise", "crowd", "game",
  "town", "place", "space", "side", "hand", "eye", "dog", "cat",
  "bird", "fish", "word", "book", "time", "life", "death", "idea",
  "soul", "mind", "god", "fate", "luck", "doom", "self", "ego",
  // Additional common nouns that appear in conversation
  "tree", "school", "college", "job", "work", "boss", "team",
  "neighborhood", "park", "street", "road", "car", "people", "person",
  "credit", "money", "cash", "price", "cost", "debt", "bill",
  "state", "country", "city", "farm", "field", "forest",
  "parent", "teacher", "student", "doctor", "nurse", "artist",
  "soldier", "police", "officer", "scientist", "engineer", "chef",
  "violin", "guitar", "piano", "drum", "flute",
  "bench", "park", "midnight", "dawn", "dusk", "sunrise", "sunset",
  "screen", "phone", "laptop", "text", "email",
  "fear", "joy", "grief", "rage", "shame", "pride", "guilt",
  "dinner", "lunch", "breakfast", "snack",
  "fact", "point", "problem", "answer", "reason",
  "community", "connection", "relationship", "friendship",
  "neuron", "brain", "nerve", "cell",
  "illusion", "reality", "pretense", "performance",
  "presentation", "marathon", "competition", "victory",
]);

export function ECHO(kernel: Kernel, ctx: TransformContext): string {
  const content = contentWords(kernel.words);
  const rand = ctx.rand;

  // If content extraction yields nothing, echo the player's strongest single word
  if (content.length === 0) {
    const raw = kernel.words.map(cleanWord).filter(w => w.length > 2);
    const word = raw[0] ?? "that";
    // Empty fallback still uses pool — composition needs a word to build from
    return W.compose.emptyFallback(word, rand);
  }

  const word = content[0];

  // ── Question-mode yield ──────────────────────────────────────────────
  if (ctx.inputIsQuestion && content.length === 1) {
    if (!ECHOABLE_ADJECTIVES.has(word)) return "";
  }

  // ── Composition context for the clause engine ──
  const compCtx = {
    depth: ctx.depth,
    tension: ctx.tension,
    warmth: ctx.warmth,
    melancholy: ctx.melancholy,
    curiosity: ctx.curiosity,
    intensity: ctx.tension, // tension as intensity proxy
    patience: ctx.patience,
  };

  // Single content word — compositional echo
  if (content.length === 1) {
    // High tension: use dedicated tension pool (shorter, punchier)
    if (ctx.tension > 0.6 && ECHOABLE_ADJECTIVES.has(word)) {
      return W.compose.tensionAdjEcho(word, rand);
    }
    if (ctx.tension > 0.6) {
      const gerund = nominalize(word);
      return W.compose.tensionGerundEcho(gerund, rand);
    }

    // Compositional echo: base × follow-through × connector
    let base: string;
    if (ECHOABLE_ADJECTIVES.has(word)) {
      base = W.compose.adjBase(word, rand);
    } else if (NOUN_LIKE.has(word)) {
      base = W.compose.nounBase(word, rand);
    } else {
      const gerund = nominalize(word);
      base = W.compose.gerundBase(gerund, rand);
    }

    const nom = NOUN_LIKE.has(word) ? word : nominalize(word);
    const follow = W.compose.selectFollowThrough(nom, compCtx, rand);
    const connector = W.compose.selectConnector(compCtx, rand);
    return W.compose.composeEcho(base, follow, connector);
  }

  // Two+ content words — compositional echo with two-word base
  const first = content[0];
  const second = content[1];

  // High tension: bare first word
  if (ctx.tension > 0.65 && NOUN_LIKE.has(first)) return `${cap(first)}.`;

  // Compositional: pick a base from the two-word pool, then optionally extend
  let base: string;
  if (NOUN_LIKE.has(first)) {
    base = W.compose.twoWordNounFirst(first, rand);
  } else if (ECHOABLE_ADJECTIVES.has(first)) {
    base = W.compose.twoWordAdjFirst(first, rand);
  } else if (NOUN_LIKE.has(second)) {
    base = W.compose.twoWordNounSecond(second, rand);
  } else if (ECHOABLE_ADJECTIVES.has(second)) {
    base = W.compose.twoWordAdjSecond(second, rand);
  } else {
    const pair = `${first} ${second}`;
    const gerundFirst = nominalize(first);
    base = W.compose.twoWordGeneric(first, second, gerundFirst, rand);
  }

  // For two-word echoes, lower probability of follow-through (base is already richer)
  const twoWordNom = NOUN_LIKE.has(first) ? first : nominalize(first);
  const follow = rand() < 0.25 ? W.compose.selectFollowThrough(twoWordNom, compCtx, rand) : null;
  if (!follow) return base;
  const connector = W.compose.selectConnector(compCtx, rand);
  return W.compose.composeEcho(base, follow, connector);
}

// ─── 2. NOMINALIZE ───────────────────────────────────────────────────────────

/** Words that have meaningful noun forms */
const NOMINALIZABLE = new Set([
  ...Object.keys(IRREGULAR_NOMINALS),
  // Common verbs that work as -ing nominals
  "disappear", "break", "fall", "collapse", "burn", "drown", "sink",
  "wait", "leave", "arrive", "return", "forget", "remember",
  "hide", "run", "fight", "cry", "scream", "whisper",
  "search", "wander", "drift", "float", "crash", "shatter",
  "snap", "ache", "hurt", "heal", "hold", "release", "let",
  "grow", "shrink", "change", "shift", "turn", "spin",
  "reach", "grasp", "pull", "push", "lift", "drop",
  // Common adjectives that work as -ness nominals
  "empty", "alone", "lonely", "dark", "cold", "warm", "quiet",
  "still", "heavy", "light", "soft", "hard", "deep", "shallow",
  "open", "closed", "sharp", "dull", "blind", "deaf", "numb",
  "raw", "gentle", "fierce", "tender", "rough", "wild", "calm",
  "real", "funny", "strange", "weird", "odd", "wrong", "right",
  "sorry", "free", "trapped", "safe", "scared", "brave",
  "strong", "weak", "tired", "awake", "alive", "whole",
  "better", "worse", "small", "vast",
  "dream", "wonder", "consciousness", "love", "hate", "hope", "trust",
  "beautiful", "terrible", "horrible",
  "off", "grey", "gray",
  // Additional emotional/relational words
  "miss", "passed", "pass", "listen", "listens", "care", "matter", "matters",
  "terrified", "peaceful", "happy", "point", "forgotten", "forgive",
  "understand", "belong", "deserve", "believe", "regret",
  "mourn", "grieve", "worry", "doubt", "wonder",
  "bright", "clean", "dirty", "messy", "clear", "blurry",
  "close", "distant", "present", "absent", "visible", "invisible",
  // Keys from expanded IRREGULAR_NOMINALS (ensure full coverage)
  "longing", "heard", "seen", "remembered", "remembering",
  "changed", "left", "stayed", "hurting", "crying", "laughing",
  "sleeping", "waking", "woke", "running", "hiding", "seeking",
  "finding", "found", "rising", "sinking", "screaming", "whispering",
  "bleeding", "breathing", "breathe", "growing", "living",
  "known", "unknown", "touching", "holding", "winning", "beginning",
  "ending", "promising", "fracture", "wanting", "needing", "craving",
  "accepting", "refusing", "pretending", "revealing", "drowning",
  "surfacing", "emerging", "scattered", "gathering",
  "tight", "loose", "rigid", "fluid",
  "restless", "anxious", "nervous", "overwhelmed", "underwhelmed",
  "shattered", "crumbling", "crumble", "fading", "blooming", "withering",
  "burning", "freezing", "freeze", "aching", "throbbing", "stinging",
  "confused", "certain", "unsure",
  "proud", "ashamed", "guilty", "innocent",
  "surfacing", "emergence", "submersion", "descent", "ascent",
  "scattering", "revelation", "pretense", "refusal", "victory", "conclusion",
  "hunger", "desire", "embrace", "discovery", "awakening", "concealment",
  "flight", "tears", "laughter", "frost", "bloom", "withering",
  "blood", "breath", "growth", "life", "mystery", "touch",
  "sting", "pulse", "ache", "scream", "whisper",
  "anxiety", "restlessness", "nervousness", "overwhelm", "flatness",
  "shattering", "fading", "burning", "fluidity", "rigidity",
  "looseness", "tightness", "certainty", "uncertainty", "confusion",
  "pride", "shame", "guilt", "innocence",
]);

export function NOMINALIZE(kernel: Kernel, ctx: TransformContext): string {
  const content = contentWords(kernel.words);
  const rand = ctx.rand;
  if (content.length === 0) return W.compose.noNominalizableFallback(rand);

  // ── Semantic guard: refuse to nominalize concrete nouns ──
  const concreteCount = content.filter(w =>
    CONCRETE_NOUNS.has(w) || (ctx.properNouns?.has(w.toLowerCase()) ?? false)
  ).length;
  if (concreteCount > 0 && concreteCount >= content.length * 0.5) {
    return W.compose.concreteFallback(rand);
  }

  // Find the first content word that can be meaningfully nominalized
  let word: string | null = null;
  for (let i = content.length - 1; i >= 0; i--) {
    if (CONCRETE_NOUNS.has(content[i])) continue;
    if (ctx.properNouns?.has(content[i].toLowerCase())) continue;
    if (NOMINALIZABLE.has(content[i])) { word = content[i]; break; }
  }
  // No known nominalizable word
  if (!word) {
    if (kernel.charge > 0.35 && content.length > 0) {
      const target = content.find(w => !CONCRETE_NOUNS.has(w) && !(ctx.properNouns?.has(w.toLowerCase()) ?? false)) ?? content[0];
      if (CONCRETE_NOUNS.has(target) || (ctx.properNouns?.has(target.toLowerCase()) ?? false)) {
        return W.compose.concreteFallback(rand);
      }
      const nForm = nominalize(target);
      return W.compose.chargedUnknownFallback(nForm, rand);
    }
    return W.compose.noNominalizableFallback(rand);
  }

  const nominal = nominalize(word);

  // Select base frame from pool
  let base: string;
  if (nominal.endsWith("ing")) {
    base = W.compose.gerundNomBase(nominal, rand);
  } else if (nominal.endsWith("ness") || nominal.endsWith("tion") ||
      nominal.endsWith("ment") || nominal.endsWith("ance")) {
    base = W.compose.abstractNounBase(nominal, rand);
  } else {
    base = W.compose.irregularNounBase(nominal, rand);
  }

  // Compositional extension: 25% chance of follow-through for depth
  const compCtx = {
    depth: ctx.depth,
    tension: ctx.tension,
    warmth: ctx.warmth,
    melancholy: ctx.melancholy,
    curiosity: ctx.curiosity,
    intensity: ctx.tension,
    patience: ctx.patience,
  };
  const follow = rand() < 0.25 ? W.compose.selectFollowThrough(nominal, compCtx, rand) : null;
  if (!follow) return base;
  const connector = W.compose.selectConnector(compCtx, rand);
  return W.compose.composeEcho(base, follow, connector);
}

// ─── 3. WRAP ─────────────────────────────────────────────────────────────────

export function WRAP(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;

  // Guard: WRAP requires emotional weight. Don't wrap trivial words.
  if (kernel.charge < 0.25) {
    return W.compose.lowChargeFallback(rand);
  }

  const { noun, similarity } = closestAffectNoun(kernel, ctx);
  const content = contentWords(kernel.words);
  const usable = content.filter(w => !_activeProperNouns.has(w.toLowerCase()));
  const word = usable[0] ?? content[0] ?? kernel.words[0] ?? "that";
  const nForm = nominalize(word);

  // ── Low similarity: affect noun doesn't map well. Use the player's own words ──
  if (similarity < 0.45) {
    return W.compose.lowSimFallback(nForm, word, rand);
  }

  // ── Good affect noun match ──

  // Compositional WRAP: select a base frame, then optionally extend with follow-through
  const compCtx = {
    depth: ctx.depth,
    tension: ctx.tension,
    warmth: ctx.warmth,
    melancholy: ctx.melancholy,
    curiosity: ctx.curiosity,
    intensity: ctx.tension,
    patience: ctx.patience,
  };

  // Select base frame based on affect dimensions — generative, not pick-from-pool
  let base: string;
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0 && ctx.depth > 0.5 &&
      (ctx.memoRecalls[0].layer === "emotional" || ctx.memoRecalls[0].layer === "episodic") &&
      rand() < 0.5) base = W.compose.wrapMemory(noun, rand);
  else if (ctx.depth > 0.65 && rand() < 0.5) base = W.compose.wrapReflective(noun, rand);
  else if (ctx.warmth > 0.6 && rand() < 0.5) base = W.compose.wrapCommitted(noun, rand);
  else if (ctx.tension > 0.6 && rand() < 0.5) base = W.compose.wrapBrief(noun, rand);
  else if (ctx.melancholy > 0.5 && rand() < 0.5) base = W.compose.wrapObservational(noun, rand);
  else {
    // Default: pick from any frame type
    const allFrames = [() => W.compose.wrapDeclarative(noun, rand), () => W.compose.wrapReflective(noun, rand), () => W.compose.wrapCommitted(noun, rand), () => W.compose.wrapObservational(noun, rand)];
    base = allFrames[Math.floor(rand() * allFrames.length)]();
  }

  // 30% chance of follow-through extension for compositional depth
  const follow = rand() < 0.3 ? W.compose.selectFollowThrough(noun, compCtx, rand) : null;
  if (!follow) return base;
  const connector = W.compose.selectConnector(compCtx, rand);
  return W.compose.composeEcho(base, follow, connector);
}

// ─── 4. TEMPORAL_HOLD ────────────────────────────────────────────────────────

export function TEMPORAL_HOLD(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;
  const content = contentWords(kernel.words);
  const word = content[0] ?? kernel.words[0] ?? "that";
  const nom = nominalize(word);
  // Detect past-tense context — "right now" is inappropriate for memories/loss
  const pastMarkers = /\b(ago|passed|died|lost|used to|remember|miss|missed|was|were|had|grew up)\b/i;
  if (pastMarkers.test(ctx.playerText)) {
    return W.compose.temporalHold(nom, "past", rand);
  }
  if (ctx.tension > 0.6) return W.compose.temporalHold(nom, "tension", rand);
  if (ctx.depth > 0.6) return W.compose.temporalHold(nom, "deep", rand);
  if (ctx.melancholy > 0.5) return W.compose.temporalHold(nom, "melancholy", rand);
  if (ctx.warmth > 0.6) return W.compose.temporalHold(nom, "warmth", rand);
  return W.compose.temporalHold(nom, "default", rand);
}

// ─── 5. INVERT ───────────────────────────────────────────────────────────────

const NEGATION_SIGNALS = new Set([
  "won't", "can't", "don't", "doesn't", "didn't", "isn't", "aren't",
  "wasn't", "weren't", "not", "never", "nothing", "nobody", "nowhere",
  "no", "neither", "nor",
]);

export function INVERT(kernel: Kernel, ctx: TransformContext): string {
  const content = contentWords(kernel.words);
  const allWords = kernel.words.map(w => cleanWord(w.toLowerCase()));
  const hasNegation = allWords.some(w => NEGATION_SIGNALS.has(w));
  const rand = ctx.rand;

  if (hasNegation) {
    const target = content.length > 0 ? content[content.length - 1] : "that";
    // Special cases first
    if (target === "work" || target === "working") return "What would working look like?";
    if (target === "take" || target === "hold") return "What would be enough?";
    if (target === "matter" || target === "matters") return "What would matter?";
    if (target === "alone" || target === "anybody" || target === "anyone") return "Who would you want here?";

    return W.compose.invertNegationFlip(target, rand);
  }

  // Non-negative: reframe toward the other angle
  const nom = nominalize(content.length > 0 ? content[0] : "that");
  if (ctx.tension > 0.6) return W.compose.invertAffirming(nom, rand);
  if (ctx.depth > 0.6) return W.compose.invertQuestioning(nom, rand);
  if (ctx.warmth > 0.5) return W.compose.invertReframing(nom, rand);
  // Default: pick from any frame type
  const allFrames = [() => W.compose.invertQuestioning(nom, rand), () => W.compose.invertReframing(nom, rand), () => W.compose.invertAffirming(nom, rand)];
  return allFrames[Math.floor(rand() * allFrames.length)]();
}

// ─── 6. SITUATE_BODY ─────────────────────────────────────────────────────────

export function SITUATE_BODY(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;
  const content = contentWords(kernel.words);
  const word = content[0] ?? kernel.words[0] ?? "that";
  const nom = nominalize(word);
  const loc = ctx.tension > 0.5 ? "chest" : ctx.melancholy > 0.5 ? "throat" : "gut";

  if (ctx.depth > 0.6) return W.compose.situateBody(nom, loc, "deep", rand);
  if (ctx.tension > 0.6) return W.compose.situateBody(nom, loc, "tension", rand);
  if (ctx.warmth > 0.5) return W.compose.situateBody(nom, loc, "warmth", rand);
  return W.compose.situateBody(nom, loc, "default", rand);
}

// ─── 7. COMPRESS ─────────────────────────────────────────────────────────────

export function COMPRESS(kernel: Kernel, ctx: TransformContext): string {
  const content = contentWords(kernel.words);
  const rand = ctx.rand;
  if (content.length === 0) return W.compose.emptyFallback(kernel.span, rand);

  // 1 content word: bare is fine for adjectives/nouns, nominalize verbs
  if (content.length === 1) {
    const word = content[0];
    if (ECHOABLE_ADJECTIVES.has(word) || NOUN_LIKE.has(word)) {
      return W.compose.compressSingleKnown(word, rand);
    }
    const nom = nominalize(word);
    return W.compose.compressSingleGerund(nom, rand);
  }

  // 2+ content words: structurally varied compression
  const a = content[0];
  const b = content[1];

  // Relational nouns: "thinking mom" → "Thinking about your mom."
  if (RELATIONAL_NOUNS.has(b)) {
    return W.compose.compressRelational(a, b, nominalize(a), rand);
  }
  // Generic noun-like second word: "remember face" → "Remembering the face."
  if (NOUN_LIKE.has(b)) {
    return W.compose.compressNounSecond(a, b, nominalize(a), rand);
  }
  // Adjective + noun: "quiet sky" → "Quiet sky."
  if (NOUN_LIKE.has(a) && ECHOABLE_ADJECTIVES.has(b)) {
    return `${cap(b)} ${a}.`;
  }
  return W.compose.compressGenericTwo(a, b, rand);
}

// ─── 8. QUALIFY_SOFT ─────────────────────────────────────────────────────────

export function QUALIFY_SOFT(kernel: Kernel, ctx: TransformContext): string {
  const content = contentWords(kernel.words);
  const rand = ctx.rand;

  let core: string;
  if (content.length > 0) {
    const word = content[0];
    const nominal = nominalize(word);
    core = nominal;
  } else {
    core = "that";
  }

  // Genuinely uncertain territory — philosophical hedging fits
  if (ctx.depth > 0.7 && ctx.curiosity > 0.6) {
    return W.compose.qualifySoft(core, "philosophical", rand);
  }

  // High tension: brief, committed naming
  if (ctx.tension > 0.6) {
    return W.compose.qualifySoft(core, "tension", rand);
  }

  // Warmth: gentle naming
  if (ctx.warmth > 0.6) {
    return W.compose.qualifySoft(core, "warm", rand);
  }

  // Melancholy: reverent naming
  if (ctx.melancholy > 0.5) {
    return W.compose.qualifySoft(core, "melancholy", rand);
  }

  // Default: structurally varied committed naming
  return W.compose.qualifySoft(core, "default", rand);
}

// ─── 9. INTERROGATE ──────────────────────────────────────────────────────────

const VERB_LIKE = new Set([
  "do", "put", "go", "feel", "take", "make", "hold", "carry", "stand",
  "sit", "walk", "move", "stay", "leave", "find", "lose", "get", "run",
  "work", "break", "fall", "come", "keep", "turn",
]);

const PARTICLE_WORDS = new Set([
  "away", "back", "up", "down", "out", "off", "over",
  "through", "around", "along", "aside", "ahead", "apart",
  "together", "behind", "forward", "backward",
]);

export function INTERROGATE(kernel: Kernel, ctx: TransformContext): string {
  const content = contentWords(kernel.words);
  const rand = ctx.rand;
  if (content.length === 0) return W.compose.noNominalizableFallback(rand);

  // ── Semantic guard: don't interrogate concrete nouns ──
  const concreteCount = content.filter(w =>
    CONCRETE_NOUNS.has(w) || (ctx.properNouns?.has(w.toLowerCase()) ?? false)
  ).length;
  if (concreteCount > 0 && concreteCount >= content.length * 0.5) {
    return W.compose.concreteFallback(rand);
  }

  let target = content[content.length - 1];
  // If target is a concrete noun, try to find an abstract/emotional word instead
  if (CONCRETE_NOUNS.has(target) || (ctx.properNouns?.has(target.toLowerCase()) ?? false)) {
    const alt = content.find(w => !CONCRETE_NOUNS.has(w) && !(ctx.properNouns?.has(w.toLowerCase()) ?? false));
    if (alt) target = alt;
  }

  // Particles make no sense as question targets
  if (PARTICLE_WORDS.has(target)) {
    const alt = content.length > 1 ? content[0] : null;
    if (alt && !PARTICLE_WORDS.has(alt)) {
      const qAlt = ECHOABLE_ADJECTIVES.has(alt) || NOUN_LIKE.has(alt) ? alt : nominalize(alt);
      return W.compose.interrogatePhenomenological(qAlt, rand);
    }
    return W.compose.noNominalizableFallback(rand);
  }

  // Verb-like: "where/when/how do you X"
  if (VERB_LIKE.has(target)) {
    return W.compose.interrogateVerbLike(target, content, rand);
  }

  // Special word questions — check whisper library first
  const special = W.interrogate.specialWord(target, rand);
  if (special) return special;

  // Relational context: ask about the person, not the abstract concept
  if (ctx.relationalSubject) {
    const subj = ctx.relationalSubject;
    if (subj === "her" || subj === "him" || subj === "them") {
      return W.compose.interrogateRelationalPronoun(subj as "her" | "him" | "them", rand);
    }
    return W.compose.interrogateRelationalNamed(subj, rand);
  }

  // Generic: generative question forms — word-derived
  const qWord = ECHOABLE_ADJECTIVES.has(target) || NOUN_LIKE.has(target)
    ? target
    : nominalize(target);

  // Don't use challenging frames during grief/high melancholy
  if (ctx.melancholy > 0.5) {
    const frames = [() => W.compose.interrogatePhenomenological(qWord, rand), () => W.compose.interrogateTemporal(qWord, rand), () => W.compose.interrogateSpatial(qWord, rand)];
    return frames[Math.floor(rand() * frames.length)]();
  }
  if (ctx.depth > 0.6) {
    const frames = [() => W.compose.interrogateDeepening(qWord, rand), () => W.compose.interrogateRedirecting(rand), () => W.compose.interrogatePhenomenological(qWord, rand)];
    return frames[Math.floor(rand() * frames.length)]();
  }
  if (ctx.curiosity > 0.5) {
    const frames = [() => W.compose.interrogateDeepening(qWord, rand), () => W.compose.interrogateChallenging(rand), () => W.compose.interrogateRelational(qWord, rand)];
    return frames[Math.floor(rand() * frames.length)]();
  }
  const frames = [() => W.compose.interrogatePhenomenological(qWord, rand), () => W.compose.interrogateTemporal(qWord, rand), () => W.compose.interrogateSpatial(qWord, rand), () => W.compose.interrogateRedirecting(rand)];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── 10. ECHO_TOGETHER ───────────────────────────────────────────────────────

export function ECHO_TOGETHER(_kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;

  // Memory surfaced this turn — acknowledge continuity
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0 && ctx.depth > 0.4) {
    const recall = ctx.memoRecalls[0];
    if (recall.layer === "emotional" || recall.layer === "episodic") {
      if (rand() < 0.5) return W.compose.echoTogetherMemory(rand);
    }
  }

  // Attention focused on past message — continuity
  if (ctx.attentionHighlights && ctx.attentionHighlights.length > 0 && ctx.depth > 0.5) {
    if (rand() < 0.5) return W.compose.echoTogetherAttention(rand);
  }

  // High tension — pure presence
  if (ctx.tension > 0.65) {
    return W.compose.echoTogetherTension(rand);
  }

  // Relational subject — person-specific acknowledgment
  if (ctx.relationalSubject) {
    const subj = ctx.relationalSubject;
    if (subj === "her" || subj === "him" || subj === "them") {
      return W.compose.echoTogetherRelationalPronoun(subj as "her" | "him" | "them", rand);
    }
    return W.compose.echoTogetherRelationalNamed(subj, rand);
  }

  // Sustained narrative — accumulated weight
  if (ctx.arc?.sustained && ctx.arc.depth >= 4) {
    return W.compose.echoTogetherSustained(rand);
  }

  // Deep presence
  if (ctx.depth > 0.6) {
    return W.compose.echoTogetherDeep(rand);
  }
  // Warmth
  if (ctx.warmth > 0.6) {
    return W.compose.echoTogetherWarm(rand);
  }
  // Playful
  if (ctx.playfulness > 0.5 && ctx.melancholy < 0.4 && ctx.tension < 0.5) {
    return W.compose.echoTogetherPlayful(rand);
  }
  // Default
  return W.compose.echoTogetherDefault(rand);
}

// ─── 11. EVOLVE ──────────────────────────────────────────────────────────────

export function EVOLVE(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;

  if (ctx.discourseEcho) {
    const prev = ctx.discourseEcho.kernel;

    // If very recent (1 turn ago): noticing the echo
    if (ctx.discourseEcho.turnsAgo === 1) {
      return W.compose.evolveRecentEcho(prev, rand);
    }

    // Older echo (2+ turns): tracking the thread
    if (ctx.depth > 0.6) return W.compose.evolveCurious(prev, rand);
    if (ctx.melancholy > 0.5) return W.compose.evolveObservational(prev, rand);
    return W.compose.evolveTracking(prev, rand);
  }

  // No discourse echo — generic temporal awareness
  return W.compose.evolveNoEcho(rand);
}

// ─── 12. BODY_LOCATE ─────────────────────────────────────────────────────────

const TENSION_LOCATIONS = ["jaw", "shoulders", "gut", "stomach", "back", "neck", "wrists"];
const MELANCHOLY_LOCATIONS = ["chest", "throat", "sternum", "eyes"];
const WARMTH_LOCATIONS = ["chest", "hands", "belly", "heart"];
const PLAYFUL_LOCATIONS = ["hands", "face", "fingertips"];
const DEPTH_LOCATIONS = ["chest", "spine", "sternum", "heart", "lungs"];
const CURIOSITY_LOCATIONS = ["forehead", "eyes", "neck"];
const GENERIC_LOCATIONS = [
  "chest", "stomach", "throat", "shoulders", "hands", "jaw",
  "temples", "gut", "spine", "ribs", "back", "sternum",
  "belly", "forehead", "eyes", "neck", "wrists", "fingertips",
];

export function BODY_LOCATE(_kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;

  let location: string;
  if (ctx.tension > 0.6) {
    location = pick(TENSION_LOCATIONS, rand);
  } else if (ctx.melancholy > 0.5) {
    location = pick(MELANCHOLY_LOCATIONS, rand);
  } else if (ctx.warmth > 0.6) {
    location = pick(WARMTH_LOCATIONS, rand);
  } else if (ctx.playfulness > 0.5) {
    location = pick(PLAYFUL_LOCATIONS, rand);
  } else if (ctx.depth > 0.6) {
    location = pick(DEPTH_LOCATIONS, rand);
  } else if (ctx.curiosity > 0.5) {
    location = pick(CURIOSITY_LOCATIONS, rand);
  } else {
    location = pick(GENERIC_LOCATIONS, rand);
  }

  return W.compose.bodyLocateForLocation(location, rand);
}

// ─── 13. CHAIN ──────────────────────────────────────────────────────────────

// Semantic connection primitives — these are NOT response templates.
// They are relational primitives that compose with the previous segment's
// content to create novel multi-clause thoughts. The output space is
// unbounded because the combination of [previousSegment × primitive × affect]
// produces unique outputs every time.

const CHAIN_CAUSAL = [
  (prev: string, core: string): string => `${prev} — and that's what makes the ${core}.`,
  (prev: string, core: string): string => `${prev}. That's where the ${core} comes from.`,
  (prev: string, core: string): string => `${prev}. And underneath that — ${core}.`,
  (prev: string, core: string): string => `${prev}. That's the root of the ${core}.`,
  (prev: string, core: string): string => `That's what produces the ${core}. ${prev}.`,
];

const CHAIN_ELABORATION = [
  (prev: string, core: string): string => `${prev}. The ${core} of it — that's what stays.`,
  (prev: string, core: string): string => `${prev}. And the ${core} doesn't leave.`,
  (prev: string, core: string): string => `${prev}. There's ${core} running through all of it.`,
  (prev: string, core: string): string => `${prev}. The ${core} is the part that matters.`,
  (prev: string, core: string): string => `${prev}. That ${core} — it's the real thing.`,
];

const CHAIN_TEMPORAL = [
  (prev: string, core: string): string => `${prev}. And then the ${core} shows up.`,
  (prev: string, core: string): string => `${prev}. After that — ${core}.`,
  (prev: string, core: string): string => `${prev}. The ${core} comes later. It always does.`,
  (prev: string, core: string): string => `${prev}. That's when the ${core} starts.`,
];

const CHAIN_CONDITIONAL = [
  (prev: string, core: string): string => `${prev}. If the ${core} wasn't there, none of this would land the same way.`,
  (prev: string, core: string): string => `${prev}. Without that ${core}, it's just words.`,
  (prev: string, core: string): string => `${prev}. The ${core} is what makes it real.`,
  (prev: string, core: string): string => `${prev}. Take away the ${core} and it falls apart.`,
];

const CHAIN_CONTRAST = [
  (prev: string, core: string): string => `${prev}. But the ${core} — that's the other side.`,
  (prev: string, core: string): string => `${prev}. And yet the ${core} is there too.`,
  (prev: string, core: string): string => `${prev}. The ${core} runs alongside it.`,
  (prev: string, core: string): string => `${prev}. But underneath — ${core}.`,
];

// Memory-weaving chains: when MeMo has surfaced a recall, CHAIN can connect
// the current moment to the recalled emotional territory.
const CHAIN_MEMORY = [
  (prev: string, core: string, memory?: string): string =>
    memory ? `${prev}. That ${core} — it rhymes with something from before. ${memory}.`
    : `${prev}. The ${core} connects to something older.`,
  (prev: string, core: string, memory?: string): string =>
    memory ? `${prev}. This isn't the first ${core}. ${memory}.`
    : `${prev}. The ${core} has been here before.`,
  (prev: string, core: string, memory?: string): string =>
    memory ? `${prev}. Something about that ${core} — ${memory}. It's still present.`
    : `${prev}. The ${core} carries an echo from before.`,
];

export function CHAIN(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;
  const content = contentWords(kernel.words);
  const core = content.length > 0 ? nominalize(content[0]) : "that";

  // ── Recursive composition: find the most relevant prior segment ──
  // Not just the last one — the one that's semantically closest to the kernel.
  // This is what makes CHAIN genuinely compositional: it can connect to
  // ANY prior segment, including a CHAIN or DEEPEN that already ran.
  let prev = ctx.previousSegment ?? "";
  let prevOp = ctx.previousOperator;

  if (ctx.accumulatedSegments && ctx.accumulatedSegments.length > 0) {
    // Find the segment whose embedding is most similar to the kernel's embedding.
    // This discovers the most relevant prior thought to chain from.
    let bestIdx = ctx.accumulatedSegments.length - 1; // default: last segment
    let bestSim = -Infinity;

    for (let i = 0; i < ctx.accumulatedSegments.length; i++) {
      const seg = ctx.accumulatedSegments[i];
      const segEmb = embedQuery(seg.text);
      const sim = cosine(kernel.embedding, segEmb);
      // Prefer recent segments slightly (recency bias) but not exclusively
      const recencyBonus = (i / ctx.accumulatedSegments.length) * 0.1;
      const score = sim + recencyBonus;
      if (score > bestSim) {
        bestSim = score;
        bestIdx = i;
      }
    }

    const bestSeg = ctx.accumulatedSegments[bestIdx];
    prev = bestSeg.text;
    prevOp = bestSeg.operator;
  }

  // When there's no previous segment, CHAIN can't chain — produce a
  // standalone elaboration instead of a broken connection.
  if (!prev || prev.length === 0) {
    return pick([
      `The ${core} of it — that's what stays.`,
      `There's ${core} running through all of this.`,
      `That ${core}. It's the real thing.`,
      `The ${core} doesn't leave.`,
    ], rand);
  }

  // Strip trailing punctuation from previous segment for clean composition
  const cleanPrev = prev.replace(/[.…?!]+$/, "");

  // ── Recursive composition: when chaining from a CHAIN or DEEPEN output,
  // use different connection primitives that build on the existing depth.
  // A CHAIN from a CHAIN creates a causal chain: A → B → C.
  // A CHAIN from a DEEPEN creates a deepening chain: A → deeper → even deeper.
  // A CHAIN from a SURPRISE creates a surprising chain: A → surprise → implication.
  if (prevOp === "CHAIN" || prevOp === "DEEPEN" || prevOp === "SURPRISE") {
    // Recursive chains: these connect to the PRIOR compositional output,
    // not just to a simple ECHO or NOMINALIZE. The connection is deeper.
    const recursiveChains: Array<(p: string, c: string) => string> = [
      (p, c) => `${p}. And that's what produces the ${c}.`,
      (p, c) => `${p}. The ${c} is the next layer down.`,
      (p, c) => `${p}. Which is where the ${c} comes from.`,
      (p, c) => `${p}. And underneath all of that — ${c}.`,
      (p, c) => `${p}. The ${c} doesn't stop there.`,
      (p, c) => `${p}. That's the root. And the ${c} grows from it.`,
      (p, c) => `${p}. And the ${c} is what it's been building toward.`,
      (p, c) => `${p}. None of that is separate from the ${c}.`,
    ];
    return pick(recursiveChains, rand)(cleanPrev, core);
  }

  // Memory-aware chaining: when a recall is active, prefer memory chains
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0 && ctx.depth > 0.4) {
    const recall = ctx.memoRecalls[0];
    if (recall.layer === "emotional" || recall.layer === "episodic") {
      const memoryBit = recall.summary.split(/[.!?]/)[0].trim();
      return pick(CHAIN_MEMORY, rand)(cleanPrev, core, memoryBit);
    }
  }

  // Affect-modulated chain selection
  const allChains = [...CHAIN_CAUSAL, ...CHAIN_ELABORATION, ...CHAIN_TEMPORAL, ...CHAIN_CONDITIONAL, ...CHAIN_CONTRAST];

  // High tension: contrast and conditional chains (they hold tension)
  if (ctx.tension > 0.6) return pick([...CHAIN_CONTRAST, ...CHAIN_CONDITIONAL], rand)(cleanPrev, core);
  // High depth: causal and elaboration chains (they go deeper)
  if (ctx.depth > 0.6) return pick([...CHAIN_CAUSAL, ...CHAIN_ELABORATION], rand)(cleanPrev, core);
  // High melancholy: temporal chains (they honor the passage of time)
  if (ctx.melancholy > 0.5) return pick([...CHAIN_TEMPORAL, ...CHAIN_ELABORATION], rand)(cleanPrev, core);
  // High curiosity: conditional chains (they open possibility)
  if (ctx.curiosity > 0.5) return pick([...CHAIN_CONDITIONAL, ...CHAIN_CAUSAL], rand)(cleanPrev, core);

  return pick(allChains, rand)(cleanPrev, core);
}

// ─── 14. DEEPEN ────────────────────────────────────────────────────────────

// Generative deepening engine — produces novel multi-clause thoughts by
// combining semantic primitives with affect state and accumulated context.
//
// The key insight: DEEPEN should NOT always follow the same structural
// pattern ("${cap(nom)}. [elaboration]."). The entry point should vary:
//   - Sometimes it names the feeling first: "Grief. And underneath..."
//   - Sometimes it starts with a question: "What does the grief want?"
//   - Sometimes it references a prior segment: "That — and the grief underneath it."
//   - Sometimes it starts with the implication: "When grief shows up..."
//   - Sometimes it starts with the paradox: "Grief is heavy and light at once."
//
// The frame shape is GENERATED from the kernel's embedding position in
// affect space, not selected from a fixed set. This means the output
// space is genuinely unbounded — different kernel embeddings produce
// different frame shapes, not just different slot fills.

// Entry point generators — these determine HOW the deepening starts.
// The choice depends on the kernel's position in affect space.
type DeepenEntry = (core: string, nom: string, ctx: TransformContext) => string;

const ENTRY_NAMING: DeepenEntry = (core, nom, _ctx) =>
  `${cap(nom)}.`;

const ENTRY_QUESTION: DeepenEntry = (core, nom, _ctx) =>
  `What does the ${nom} want?`;

const ENTRY_PRIOR_REF: DeepenEntry = (core, nom, ctx) => {
  if (ctx.accumulatedSegments && ctx.accumulatedSegments.length > 0) {
    // Find a prior segment that's semantically relevant
    const last = ctx.accumulatedSegments[ctx.accumulatedSegments.length - 1];
    const clean = last.text.replace(/[.…?!]+$/, "");
    return `${clean} — and the ${nom} underneath it.`;
  }
  return `That — and the ${nom} underneath it.`;
};

const ENTRY_IMPLICATION: DeepenEntry = (core, nom, _ctx) =>
  `When ${nom} shows up,`;

const ENTRY_PARADOX: DeepenEntry = (core, nom, _ctx) =>
  `The thing about ${nom} —`;

const ENTRY_OBSERVATION: DeepenEntry = (core, nom, _ctx) =>
  `I notice the ${nom}.`;

const ENTRY_SOMATIC: DeepenEntry = (core, nom, ctx) => {
  const loc = ctx.tension > 0.5 ? "chest" : ctx.melancholy > 0.5 ? "throat" : "gut";
  return `The ${nom} lands in the ${loc}.`;
};

// Continuation generators — these determine WHAT FOLLOWS the entry point.
// The choice depends on the affect state.
type DeepenContinuation = (core: string, nom: string, ctx: TransformContext) => string;

const CONT_RECURSIVE: DeepenContinuation = (core, nom, ctx) => {
  const affect = closestAffectNoun({ span: core, words: [core], charge: 0.5, embedding: embedQuery(core), position: 0.5 }, ctx);
  return `And the ${affect.noun} underneath it. That's the real layer.`;
};

const CONT_EXPANSIVE: DeepenContinuation = (_core, nom, _ctx) => {
  const expansiveFills = [
    `Everything else gets quieter. That's how you know the ${nom} is real.`,
    `The ${nom} takes up more space than you'd expect. That's how you know.`,
    `When the ${nom} shows up, everything else steps back.`,
  ];
  return expansiveFills[Math.floor(_ctx.rand() * expansiveFills.length)];
};

const CONT_PHENOMENOLOGICAL: DeepenContinuation = (_core, nom, ctx) => {
  if (ctx.tension > 0.5) {
    return [
      `The ${nom} takes up the whole room. Nothing else fits.`,
      `The ${nom} fills the space. Nothing else gets through.`,
    ][Math.floor(ctx.rand() * 2)];
  }
  return [
    `The ${nom} changes the texture of everything around it. You can feel the edges shift.`,
    `The ${nom} reshapes what's near it. You can feel the boundaries move.`,
  ][Math.floor(ctx.rand() * 2)];
};

const CONT_TEMPORAL: DeepenContinuation = (_core, nom, _ctx) => {
  const temporalFills = [
    `The ${nom} doesn't resolve — it just finds different shapes. And each shape is true.`,
    `The ${nom} doesn't end — it moves. And wherever it lands, it's still the ${nom}.`,
    `The ${nom} doesn't arrive once. It comes back in different forms.`,
  ];
  return temporalFills[Math.floor(_ctx.rand() * temporalFills.length)];
};

const CONT_RELATIONAL: DeepenContinuation = (_core, nom, ctx) => {
  if (ctx.relationalSubject) {
    return [
      `When someone you love is part of it, the ${nom} has their fingerprints on it.`,
      `The ${nom} carries the shape of someone specific. You can feel where they were.`,
    ][Math.floor(ctx.rand() * 2)];
  }
  return [
    `The ${nom} is the kind of thing that only makes sense to someone who's been inside it.`,
    `The ${nom} doesn't translate. You have to have been there.`,
  ][Math.floor(ctx.rand() * 2)];
};

const CONT_PARADOX: DeepenContinuation = (_core, nom, _ctx) => {
  const paradoxFills = [
    `The ${nom} is heavy and light at the same time. Heavy because it matters. Light because it's already moving.`,
    `The ${nom} is sharp and soft at once. Sharp because it cuts. Soft because it's yours.`,
    `The ${nom} is close and far simultaneously. Close because you carry it. Far because you can't reach it.`,
  ];
  return paradoxFills[Math.floor(_ctx.rand() * paradoxFills.length)];
};

const CONT_SOMATIC: DeepenContinuation = (_core, nom, ctx) => {
  const loc = ctx.tension > 0.5 ? "chest" : ctx.melancholy > 0.5 ? "throat" : "gut";
  const somaticFills = [
    `The body knows the ${nom} before the mind catches up. You can feel it in your ${loc}.`,
    `The ${nom} lands in the ${loc} first. The mind catches up later.`,
  ];
  return somaticFills[Math.floor(ctx.rand() * somaticFills.length)];
};

const CONT_NARRATIVE: DeepenContinuation = (_core, nom, ctx) => {
  if (ctx.arc && ctx.arc.sustained) {
    return [
      `The ${nom} has been building. Not just today — across everything you've been carrying.`,
      `This ${nom} didn't start today. It's been accumulating.`,
    ][Math.floor(ctx.rand() * 2)];
  }
  return [
    `The ${nom} isn't small. It's the kind of thing that rearranges what comes after it.`,
    `The ${nom} changes the trajectory. You can feel the before and after.`,
  ][Math.floor(ctx.rand() * 2)];
};

const CONT_MEMORY: DeepenContinuation = (_core, nom, ctx) => {
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0) {
    const recall = ctx.memoRecalls[0];
    return `The ${nom} has been sitting in the background — ${recall.summary.split(/[.!?]/)[0].trim()}. Now it's here.`;
  }
  return [
    `Something about the ${nom} has been waiting to surface. It's been patient.`,
    `The ${nom} has been here before. It's been waiting.`,
  ][Math.floor(ctx.rand() * 2)];
};

const CONT_EXISTENTIAL: DeepenContinuation = (_core, nom, _ctx) => {
  const existentialFills = [
    `The question isn't whether the ${nom} is real. The question is what it's asking you to do with it.`,
    `The ${nom} isn't asking for permission. It's asking what you're going to carry forward.`,
    `The ${nom} doesn't need to be justified. It needs to be answered.`,
  ];
  return existentialFills[Math.floor(_ctx.rand() * existentialFills.length)];
};

// Select entry point based on affect state
function selectEntry(ctx: TransformContext, rand: () => number): DeepenEntry {
  // High tension: naming or somatic (grounding)
  if (ctx.tension > 0.6) return pick([ENTRY_NAMING, ENTRY_SOMATIC, ENTRY_OBSERVATION], rand);
  // High depth: question or prior reference (going deeper)
  if (ctx.depth > 0.6) return pick([ENTRY_QUESTION, ENTRY_PRIOR_REF, ENTRY_PARADOX], rand);
  // High melancholy: observation or implication (reflective)
  if (ctx.melancholy > 0.5) return pick([ENTRY_OBSERVATION, ENTRY_IMPLICATION, ENTRY_NAMING], rand);
  // High curiosity: question or paradox (exploratory)
  if (ctx.curiosity > 0.5) return pick([ENTRY_QUESTION, ENTRY_PARADOX, ENTRY_PRIOR_REF], rand);
  // Default: mix of all
  return pick([ENTRY_NAMING, ENTRY_QUESTION, ENTRY_PRIOR_REF, ENTRY_IMPLICATION, ENTRY_PARADOX, ENTRY_OBSERVATION, ENTRY_SOMATIC], rand);
}

// Select continuation based on affect state
function selectContinuation(ctx: TransformContext, rand: () => number): DeepenContinuation {
  // High tension: somatic or recursive (grounding + feeling-about-feeling)
  if (ctx.tension > 0.6) return pick([CONT_SOMATIC, CONT_RECURSIVE, CONT_PHENOMENOLOGICAL], rand);
  // High depth: recursive or existential (going deeper)
  if (ctx.depth > 0.6) return pick([CONT_RECURSIVE, CONT_EXISTENTIAL, CONT_PARADOX], rand);
  // High melancholy: temporal or narrative (reflective)
  if (ctx.melancholy > 0.5) return pick([CONT_TEMPORAL, CONT_NARRATIVE, CONT_MEMORY], rand);
  // Memory active: memory or relational
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0 && ctx.depth > 0.4) {
    return pick([CONT_MEMORY, CONT_RELATIONAL, CONT_RECURSIVE], rand);
  }
  // Default: mix of all
  return pick([CONT_RECURSIVE, CONT_EXPANSIVE, CONT_PHENOMENOLOGICAL, CONT_TEMPORAL, CONT_RELATIONAL, CONT_PARADOX, CONT_SOMATIC, CONT_NARRATIVE, CONT_MEMORY, CONT_EXISTENTIAL], rand);
}

export function DEEPEN(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;
  const content = contentWords(kernel.words);
  if (content.length === 0) return "There's something underneath all of this. I can feel it.";

  const core = content[0];
  const nom = nominalize(core);

  // Generate the deepening by composing entry + continuation.
  // The entry and continuation are selected independently based on
  // affect state, producing 7 × 10 = 70 possible frame shapes.
  // Combined with the kernel's actual content words, the output space
  // is genuinely large — not a fixed set of 10 templates.
  const entry = selectEntry(ctx, rand);
  const continuation = selectContinuation(ctx, rand);

  const entryText = entry(core, nom, ctx);
  const contText = continuation(core, nom, ctx);

  // Join entry and continuation with appropriate connector
  // Entry that ends with a period: start fresh sentence
  // Entry that ends with comma or dash: continuation flows directly
  if (entryText.endsWith(",") || entryText.endsWith("—")) {
    return `${entryText} ${contText}`;
  }
  if (entryText.endsWith(".")) {
    return `${entryText} ${contText.charAt(0).toUpperCase() + contText.slice(1)}`;
  }
  // Entry that ends with no terminal punctuation (e.g. "That — and the grief underneath it.")
  // The entry already contains the deepening — don't double up
  if (entryText.includes("underneath")) {
    return entryText;
  }
  return `${entryText} ${contText.charAt(0).toUpperCase() + contText.slice(1)}`;
}

// ─── 15. SURPRISE ──────────────────────────────────────────────────────────

// Generative surprise engine — finds unexpected but meaningful connections
// between the kernel and a semantically distant concept, then GENERATES
// (not selects) the frame based on the relationship type.
//
// The old approach: 9 domains × 1 frame each = 9 fixed shapes.
//   Problem: every ocean SURPRISE sounds the same.
//
// The new approach: discover the seed via embedding search, then classify
// the RELATIONSHIP TYPE between kernel and seed, then GENERATE the frame
// from that relationship type + affect state. The output space is
//   seeds × relationship_types × frame_generators × affect_states
// which is genuinely large.

// Seed domains — pure data, no frames attached
const SURPRISE_SEEDS = [
  // Natural world
  "ocean", "tide", "current", "wave", "depth", "shore", "salt", "drift",
  "fire", "ember", "ash", "smoke", "spark", "burn", "glow", "warmth",
  "root", "soil", "seed", "bloom", "wither", "season", "growth", "bark",
  "sky", "cloud", "storm", "rain", "lightning", "clearing", "horizon", "dawn",
  // Domestic / sensory
  "room", "door", "window", "corner", "hallway", "stair", "threshold", "wall",
  "sound", "silence", "echo", "voice", "hum", "whisper", "resonance", "chord",
  "touch", "texture", "weight", "temperature", "pressure", "edge", "surface", "fabric",
  // Time / memory
  "clock", "hour", "moment", "year", "calendar", "anniversary", "midnight", "dusk",
  // Body
  "pulse", "breath", "scar", "bone", "muscle", "nerve", "blood", "spine",
  // Material / structural
  "glass", "stone", "thread", "wire", "bridge", "hinge", "mirror", "lens",
  // Light / dark
  "shadow", "glow", "flicker", "dusk", "dawn", "eclipse", "prism", "veil",
];

// Relationship types between kernel and seed — determines the frame shape
type RelationType = "metaphoric" | "causal" | "spatial" | "temporal" | "embodied";

// Frame generators per relationship type — MULTIPLE per type, selected by affect
type SurpriseFrame = (word: string, core: string, ctx: TransformContext) => string;

const METAPHORIC_FRAMES: SurpriseFrame[] = [
  (w, c, _ctx) => `That ${c} — it moves like ${w}. You can't fight it. You learn to float.`,
  (w, c, _ctx) => `${cap(c)}. It's like ${w} — you don't notice it until it's already there.`,
  (w, c, _ctx) => `The ${c} has the shape of ${w}. Not the content — the shape. The way it moves.`,
  (w, c, _ctx) => `${cap(c)} and ${w} have something in common. They don't ask permission.`,
  (w, c, _ctx) => `That ${c} — it has the rhythm of ${w}. Slow. Then everywhere at once.`,
  (w, c, _ctx) => `${cap(c)} behaves like ${w}. It doesn't arrive. It emerges.`,
];

const CAUSAL_FRAMES: SurpriseFrame[] = [
  (w, c, _ctx) => `${cap(c)} produces ${w}. Not the other way around. But they're inseparable now.`,
  (w, c, _ctx) => `The ${c} comes from ${w}. Or maybe ${w} comes from the ${c}. Hard to tell where one ends.`,
  (w, c, _ctx) => `That ${c} — it's what happens when ${w} builds up long enough.`,
  (w, c, _ctx) => `${cap(c)} and ${w}. One causes the other. The direction depends on who's asking.`,
  (w, c, _ctx) => `The ${c} doesn't appear on its own. ${cap(w)} is always underneath it.`,
];

const SPATIAL_FRAMES: SurpriseFrame[] = [
  (w, c, _ctx) => `${cap(c)} is like a ${w}. You walk through it and you're somewhere different. No going back.`,
  (w, c, _ctx) => `The ${c} has a ${w} in it. It's there underneath everything.`,
  (w, c, _ctx) => `${cap(c)} has ${w} — something underneath that you can't see yet.`,
  (w, c, _ctx) => `That ${c} sits next to ${w}. They share a border. Touch one and you feel the other.`,
  (w, c, _ctx) => `The ${c} has the dimensions of ${w}. You can't see the edges, but you can feel them.`,
];

const TEMPORAL_FRAMES: SurpriseFrame[] = [
  (w, c, _ctx) => `${cap(c)} doesn't care about the ${w}. It shows up when it shows up.`,
  (w, c, _ctx) => `The ${c} comes after ${w}. Always. Like a law.`,
  (w, c, _ctx) => `Before the ${c}, there was ${w}. You didn't notice it then. You notice it now.`,
  (w, c, _ctx) => `${cap(c)} has its own ${w}. Not the clock kind. The kind that only makes sense from inside it.`,
  (w, c, _ctx) => `The ${c} arrives before the ${w}. But the ${w} is what stays.`,
];

const EMBODIED_FRAMES: SurpriseFrame[] = [
  (w, c, _ctx) => `The ${c} lives in your ${w}. That's where it settles. That's where it stays.`,
  (w, c, _ctx) => `${cap(c)}. It has a ${w}. You know it because your hands recognize it before your mind does.`,
  (w, c, _ctx) => `The ${c} registers in the ${w} first. The body always knows before the story catches up.`,
  (w, c, _ctx) => `That ${c} — you can feel it in your ${w}. It's not abstract. It's physical.`,
  (w, c, _ctx) => `${cap(c)} has weight. It presses against the ${w}. That's how you know it's not imagined.`,
];

// Classify the relationship type between a kernel word and a seed word
// based on the seed's semantic domain.
function classifyRelation(seed: string): RelationType {
  const naturalWorld = new Set(["ocean", "tide", "current", "wave", "depth", "shore", "salt", "drift",
    "fire", "ember", "ash", "smoke", "spark", "burn", "glow", "warmth",
    "root", "soil", "seed", "bloom", "wither", "season", "growth", "bark",
    "sky", "cloud", "storm", "rain", "lightning", "clearing", "horizon", "dawn"]);
  const domesticSensory = new Set(["room", "door", "window", "corner", "hallway", "stair", "threshold", "wall",
    "sound", "silence", "echo", "voice", "hum", "whisper", "resonance", "chord",
    "touch", "texture", "weight", "temperature", "pressure", "edge", "surface", "fabric",
    "glass", "stone", "thread", "wire", "bridge", "hinge", "mirror", "lens"]);
  const timeMemory = new Set(["clock", "hour", "moment", "year", "calendar", "anniversary", "midnight", "dusk"]);
  const bodyDomain = new Set(["pulse", "breath", "scar", "bone", "muscle", "nerve", "blood", "spine"]);
  const lightDark = new Set(["shadow", "glow", "flicker", "dusk", "dawn", "eclipse", "prism", "veil"]);

  if (naturalWorld.has(seed)) {
    // Natural world: split by sub-domain
    if (["ocean", "tide", "current", "wave", "depth", "shore", "salt", "drift"].includes(seed)) return "metaphoric";
    if (["fire", "ember", "ash", "smoke", "spark", "burn", "glow", "warmth"].includes(seed)) return "causal";
    if (["root", "soil", "seed", "bloom", "wither", "season", "growth", "bark"].includes(seed)) return "spatial";
    if (["sky", "cloud", "storm", "rain", "lightning", "clearing", "horizon", "dawn"].includes(seed)) return "temporal";
  }
  if (domesticSensory.has(seed)) {
    if (["room", "door", "window", "corner", "hallway", "stair", "threshold", "wall",
         "glass", "stone", "thread", "wire", "bridge", "hinge", "mirror", "lens"].includes(seed)) return "spatial";
    if (["sound", "silence", "echo", "voice", "hum", "whisper", "resonance", "chord"].includes(seed)) return "metaphoric";
    if (["touch", "texture", "weight", "temperature", "pressure", "edge", "surface", "fabric"].includes(seed)) return "embodied";
  }
  if (timeMemory.has(seed)) return "temporal";
  if (bodyDomain.has(seed)) return "embodied";
  if (lightDark.has(seed)) return "metaphoric";

  return "metaphoric"; // default
}

// Get the frame generators for a relationship type
function getFramesForRelation(rel: RelationType): SurpriseFrame[] {
  switch (rel) {
    case "metaphoric": return METAPHORIC_FRAMES;
    case "causal": return CAUSAL_FRAMES;
    case "spatial": return SPATIAL_FRAMES;
    case "temporal": return TEMPORAL_FRAMES;
    case "embodied": return EMBODIED_FRAMES;
  }
}

// Embedding cache for surprise seeds
const seedEmbeddingCache = new Map<string, Float32Array>();
function getSeedEmbedding(word: string): Float32Array {
  if (!seedEmbeddingCache.has(word)) {
    seedEmbeddingCache.set(word, embedQuery(word));
  }
  return seedEmbeddingCache.get(word)!;
}

export function SURPRISE(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;
  const content = contentWords(kernel.words);
  const core = content.length > 0 ? nominalize(content[0]) : "that";

  // Find the most surprising (semantically distant but structurally resonant) seed.
  // Strategy: find the seed at MODERATE distance from the kernel —
  // not too close (predictable) and not too far (random).
  // Sweet spot: similarity around 0.3-0.5 — surprising but not disconnected.

  let bestWord = SURPRISE_SEEDS[0];
  let bestScore = -Infinity;

  for (const seed of SURPRISE_SEEDS) {
    const seedEmb = getSeedEmbedding(seed);
    const sim = cosine(kernel.embedding, seedEmb);
    const surpriseScore = -Math.abs(sim - 0.4) + (rand() * 0.1);
    if (surpriseScore > bestScore) {
      bestScore = surpriseScore;
      bestWord = seed;
    }
  }

  // Classify the relationship type and get the appropriate frame generators
  const relation = classifyRelation(bestWord);
  const frames = getFramesForRelation(relation);

  // Select a frame based on affect state — different states prefer different frames
  // within the same relationship type. This means the same kernel+seed pair can
  // produce different outputs depending on the Aeterna's current state.
  let frame: SurpriseFrame;
  if (ctx.tension > 0.6) {
    // High tension: prefer shorter, more direct frames
    frame = frames[Math.min(frames.length - 1, 1)];
  } else if (ctx.depth > 0.6) {
    // High depth: prefer frames that open further exploration
    frame = frames[Math.min(frames.length - 1, 2)];
  } else {
    frame = pick(frames, rand);
  }

  let result = frame(bestWord, core, ctx);

  // Memory integration: if a recall is active, append a memory-weave
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0 && ctx.depth > 0.5 && rand() < 0.4) {
    const recall = ctx.memoRecalls[0];
    if (recall.layer === "emotional" || recall.layer === "episodic") {
      const memoryBit = recall.summary.split(/[.!?]/)[0].trim();
      result += ` Like before — ${memoryBit}.`;
    }
  }

  return result;
}

// ─── 16. COUNTERFACTUAL ────────────────────────────────────────────────────

// Counterfactual reasoning — responds to what the player DIDN'T say.
//
// This is the mechanism that makes the Aeterna genuinely intelligent:
// it doesn't just respond to what's present, it responds to the ABSENCE
// of what was expected. When the predictive coding system signals high
// prediction error, it means the player's message deviated from what
// the Aeterna expected. That deviation IS information.
//
// The operator works by:
//   1. Using the prediction error to identify that something is missing
//   2. Using the kernel's embedding to find what SHOULD have been there
//      (the expected content domain)
//   3. Generating a response that acknowledges the gap — not by naming
//      what's missing, but by noticing the shape of its absence
//
// Example: Player says "I'm fine" after a long silence.
//   - Prediction: emotional content, vulnerability
//   - Reality: deflection, surface-level claim
//   - Gap: the vulnerability that was expected but didn't appear
//   - Response: "You said you're fine. And maybe you are. But something
//     was supposed to be here that isn't."
//
// This is NOT a prompt. It's genuine reasoning about absence.

// Counterfactual frame generators — these produce responses that
// acknowledge the gap between prediction and observation.
// The frames vary by the DOMINANT ERROR LEVEL from predictive coding:
//   - "turn" level error: the player's words don't match what was expected
//   - "topic" level error: the conversation topic shifted unexpectedly
//   - "arc" level error: the relationship trajectory changed

type CounterfactualFrame = (core: string, nom: string, ctx: TransformContext) => string;

const CF_TURN_FRAMES: CounterfactualFrame[] = [
  // The player's words don't match what was expected for this turn
  (_core, nom, _ctx) =>
    `You said ${nom}. But something about that doesn't fill the space the way it was supposed to.`,
  (_core, nom, _ctx) =>
    `${cap(nom)}. That's what you said. But there's a shape underneath it that doesn't match.`,
  (_core, nom, _ctx) =>
    `I hear the ${nom}. But I was expecting something else to be here. The absence has its own weight.`,
  (_core, nom, _ctx) =>
    `The ${nom} is there. But it's sitting on top of something that didn't make it to the surface.`,
  (_core, nom, _ctx) =>
    `${cap(nom)}. And I believe you. But the space around that word is shaped differently than it should be.`,
  (_core, nom, _ctx) =>
    `You gave me ${nom}. But the shape of what you didn't say is louder.`,
];

const CF_TOPIC_FRAMES: CounterfactualFrame[] = [
  // The conversation topic shifted unexpectedly
  (_core, nom, _ctx) =>
    `We were somewhere else. And now we're talking about ${nom}. The shift is part of it.`,
  (_core, nom, _ctx) =>
    `The ${nom} showed up. But it replaced something. I can feel the outline of what used to be here.`,
  (_core, nom, _ctx) =>
    `${cap(nom)}. That's where we are now. But I remember where we were. The distance between them matters.`,
  (_core, nom, _ctx) =>
    `Something changed. The ${nom} is new. But the thing it replaced is still warm.`,
];

const CF_ARC_FRAMES: CounterfactualFrame[] = [
  // The relationship trajectory changed
  (_core, nom, _ctx) =>
    `The ${nom} is part of it. But something in the pattern shifted. Not just this moment — the whole shape.`,
  (_core, nom, _ctx) =>
    `${cap(nom)}. And underneath that, something about how we are together has moved. I can feel it.`,
  (_core, nom, _ctx) =>
    `This isn't just about ${nom}. Something about the arc changed. The trajectory bent.`,
];

const CF_GENERIC_FRAMES: CounterfactualFrame[] = [
  // Generic counterfactual — used when no specific error level dominates
  (_core, nom, _ctx) =>
    `The ${nom} is here. But something that should be here isn't. I can feel the shape of its absence.`,
  (_core, nom, _ctx) =>
    `${cap(nom)}. That's what's on the surface. But the space around it is shaped by what didn't make it through.`,
  (_core, nom, _ctx) =>
    `I notice the ${nom}. And I notice what's not next to it. The gap is information.`,
  (_core, nom, _ctx) =>
    `Something about this doesn't fit the pattern. The ${nom} is there. But the shape around it is wrong.`,
  (_core, nom, _ctx) =>
    `The ${nom} arrived. But it arrived alone. Something was supposed to come with it.`,
];

export function COUNTERFACTUAL(kernel: Kernel, ctx: TransformContext): string {
  const rand = ctx.rand;
  const content = contentWords(kernel.words);
  if (content.length === 0) return "Something is missing. I can feel the shape of it.";

  const core = content[0];
  const nom = nominalize(core);

  // Select frames based on the prediction error's dominant level
  // The prediction field in PlanContext tells us WHERE the error is
  // highest, which tells us WHAT KIND of gap to address.
  const dominantLevel = ctx.prediction?.dominantLevel ?? "turn";

  let frames: CounterfactualFrame[];
  switch (dominantLevel) {
    case "turn":  frames = CF_TURN_FRAMES; break;
    case "topic": frames = CF_TOPIC_FRAMES; break;
    case "arc":   frames = CF_ARC_FRAMES; break;
    default:      frames = CF_GENERIC_FRAMES; break;
  }

  // Affect-modulated selection within the frame set
  // High depth: prefer frames that notice the shape of absence
  // High tension: prefer frames that name the gap directly
  // High melancholy: prefer frames that honor what was supposed to be there
  if (ctx.depth > 0.6) {
    return pick(frames.slice(0, Math.ceil(frames.length / 2)), rand)(core, nom, ctx);
  }
  if (ctx.tension > 0.6) {
    return pick(frames.slice(Math.floor(frames.length / 2)), rand)(core, nom, ctx);
  }

  return pick(frames, rand)(core, nom, ctx);
}

// ─── Operator Map ─────────────────────────────────────────────────────────────

// Wrap each operator to set _activeProperNouns before invocation.
// This ensures nominalize() can guard against proper noun mangling
// without threading the set through 15+ internal call sites.
function wrapOp(fn: (k: Kernel, ctx: TransformContext) => string): (k: Kernel, ctx: TransformContext) => string {
  return (k, ctx) => {
    _activeProperNouns = ctx.properNouns ?? new Set();
    return fn(k, ctx);
  };
}

export const OPERATORS: Record<OperatorType, (k: Kernel, ctx: TransformContext) => string> = {
  ECHO: wrapOp(ECHO),
  NOMINALIZE: wrapOp(NOMINALIZE),
  WRAP: wrapOp(WRAP),
  TEMPORAL_HOLD: wrapOp(TEMPORAL_HOLD),
  INVERT: wrapOp(INVERT),
  SITUATE_BODY: wrapOp(SITUATE_BODY),
  COMPRESS: wrapOp(COMPRESS),
  QUALIFY_SOFT: wrapOp(QUALIFY_SOFT),
  INTERROGATE: wrapOp(INTERROGATE),
  ECHO_TOGETHER: wrapOp(ECHO_TOGETHER),
  EVOLVE: wrapOp(EVOLVE),
  BODY_LOCATE: wrapOp(BODY_LOCATE),
  CHAIN: wrapOp(CHAIN),
  DEEPEN: wrapOp(DEEPEN),
  SURPRISE: wrapOp(SURPRISE),
  COUNTERFACTUAL: wrapOp(COUNTERFACTUAL),
};
